/* @ts-self-types="./sim_wasm.d.ts" */

/**
 * The vertex buffers for one chunk mesh.
 *
 * Produced by [`WasmSim::mesh_chunk`](crate::WasmSim::mesh_chunk). An empty
 * mesh is a valid and common answer — a slab of pure air, a slab sealed inside
 * rock, and a chunk the world has not generated all produce one.
 *
 * # The four buffer methods copy
 *
 * [`positions`](ChunkMesh::positions), [`normals`](ChunkMesh::normals),
 * [`colors`](ChunkMesh::colors) and [`indices`](ChunkMesh::indices) are methods
 * rather than properties on purpose: each call allocates a fresh JS typed array
 * and copies into it, and a property that costs a memcpy reads like a field
 * access. Call each once per chunk, hand the arrays to `BufferAttribute`, and
 * `free()` the mesh.
 *
 * The counts are properties, because they are already scalars.
 *
 * `Debug` summarises rather than printing a few thousand floats — `MeshBuffers`
 * already does the summarising (guide §5).
 */
export class ChunkMesh {
    static __wrap(ptr) {
        const obj = Object.create(ChunkMesh.prototype);
        obj.__wbg_ptr = ptr;
        ChunkMeshFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ChunkMeshFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_chunkmesh_free(ptr, 0);
    }
    /**
     * Returns the vertex colours: three floats per vertex, **linear** RGB in
     * 0..1.
     *
     * Linear because that has been Three.js's working colour space since r152
     * (`docs/research/rendering/threejs-chunks.md` §3); the sRGB bytes in
     * `data/materials.toml` are converted by the mesher's lookup table, so the
     * renderer uploads these untouched.
     * @returns {Float32Array}
     */
    colors() {
        const ret = wasm.chunkmesh_colors(this.__wbg_ptr);
        var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Returns the triangle indices: three per triangle, counter-clockwise seen
     * from outside the solid.
     *
     * Always 32-bit, even for a mesh that would fit in 16: one width for one
     * contract.
     * @returns {Uint32Array}
     */
    indices() {
        const ret = wasm.chunkmesh_indices(this.__wbg_ptr);
        var v1 = getArrayU32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Whether the mesh has no triangles.
     *
     * The cheap question. A streaming client asks it before allocating a
     * geometry, and answering it must not cost four buffer copies.
     * @returns {boolean}
     */
    get is_empty() {
        const ret = wasm.chunkmesh_is_empty(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Returns the vertex normals: three floats per vertex, unit length.
     * @returns {Float32Array}
     */
    normals() {
        const ret = wasm.chunkmesh_normals(this.__wbg_ptr);
        var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Returns the vertex positions: three floats per vertex, in world tile
     * units with z up.
     *
     * World-space, not chunk-local — the mesher knows the chunk's origin and
     * making the renderer add it back would be one more place to get it wrong
     * (`web/src/world/types.ts`).
     * @returns {Float32Array}
     */
    positions() {
        const ret = wasm.chunkmesh_positions(this.__wbg_ptr);
        var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * How many triangles the mesh has.
     * @returns {number}
     */
    get triangle_count() {
        const ret = wasm.chunkmesh_triangle_count(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * How many vertices the mesh has.
     * @returns {number}
     */
    get vertex_count() {
        const ret = wasm.chunkmesh_vertex_count(this.__wbg_ptr);
        return ret >>> 0;
    }
}
if (Symbol.dispose) ChunkMesh.prototype[Symbol.dispose] = ChunkMesh.prototype.free;

/**
 * One line, said by somebody, about one record.
 *
 * What [`WasmSim::speak`] and [`WasmSim::speak_flight`] hand back: the sentence
 * itself, who said it, and — the part that makes it a channel rather than
 * flavour — **which record it is about**. A client puts that id in the player's
 * journal and a debug view resolves it against the log
 * ([`WasmSim::describe`]), which is the phase's traceability claim in one
 * object.
 *
 * A wasm-bindgen object, so `free()` it once the line is on screen. One is
 * created per interaction, which is a rate a finalizer could keep up with; the
 * habit is worth keeping anyway.
 */
export class Spoken {
    static __wrap(ptr) {
        const obj = Object.create(Spoken.prototype);
        obj.__wbg_ptr = ptr;
        SpokenFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        SpokenFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_spoken_free(ptr, 0);
    }
    /**
     * The record the line is about.
     * @returns {number}
     */
    get about() {
        const ret = wasm.spoken_about(this.__wbg_ptr);
        return ret;
    }
    /**
     * Who said it, or `-1` when the speaker is a group rather than a person.
     * @returns {number}
     */
    get figure() {
        const ret = wasm.spoken_figure(this.__wbg_ptr);
        return ret;
    }
    /**
     * How many mouths the news came through to reach this speaker.
     *
     * Zero is somebody who was there. It is what the sentence's hedging was
     * built from and what a debug view shows beside it; it is *not* a measure
     * of how true the record is, because a record does not become less true by
     * being repeated (Phase 3 plan, decision 2).
     * @returns {number}
     */
    get hops() {
        const ret = wasm.spoken_hops(this.__wbg_ptr);
        return ret;
    }
    /**
     * What day of that year it learned it.
     * @returns {number}
     */
    get learned_day_of_year() {
        const ret = wasm.spoken_learned_day_of_year(this.__wbg_ptr);
        return ret;
    }
    /**
     * What year this speaker's settlement learned it.
     * @returns {number}
     */
    get learned_year() {
        const ret = wasm.spoken_learned_year(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * What to put above the line: a name with whatever byname the log earned
     * them, or a place a column of refugees walked out of.
     * @returns {string}
     */
    get speaker() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.spoken_speaker(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The line itself, exactly as `sim_core` composed it.
     * @returns {string}
     */
    get text() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.spoken_text(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
}
if (Symbol.dispose) Spoken.prototype[Symbol.dispose] = Spoken.prototype.free;

/**
 * What one tick reports back.
 *
 * `sim_core::TickOutput` as JavaScript can hold it: the scalars a HUD shows,
 * and the two bulk lists a renderer steps on — which chunks changed, and what
 * happened. One object with owned buffers, so `free()` releases the whole tick
 * at once and a host that drops it on the floor leaks one finalizable thing
 * rather than four.
 *
 * # Reading it
 *
 * The scalars are properties and cost nothing. **The arrays are methods and
 * each call copies**, so call each at most once per tick, and use the
 * [`dirty_chunk_count`](Self::dirty_chunk_count) and
 * [`dug_count`](Self::dug_count) properties to skip the copy entirely on the
 * many ticks where nothing changed:
 *
 * ```js
 * const summary = sim.tick();
 *
 * if (summary.dirty_chunk_count > 0) {
 *   const dirty = summary.dirty_chunks(); // Int32Array of cx, cy, z triples
 *   // …remesh
 * }
 *
 * summary.free();
 * ```
 *
 * The flattened layouts are stated once in `crate::flatten` and are the
 * contract `web/src/world/types.ts` transcribes.
 *
 * The `Debug` spelling of the hash is the raw `u64`; the exported one is hex
 * (`crate::convert`).
 */
export class TickSummary {
    static __wrap(ptr) {
        const obj = Object.create(TickSummary.prototype);
        obj.__wbg_ptr = ptr;
        TickSummaryFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        TickSummaryFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_ticksummary_free(ptr, 0);
    }
    /**
     * How many chunks the world has generated.
     * @returns {number}
     */
    get chunk_count() {
        const ret = wasm.ticksummary_chunk_count(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * What day of the year it is after the tick, counting from zero.
     * @returns {number}
     */
    get date_day_of_year() {
        const ret = wasm.ticksummary_date_day_of_year(this.__wbg_ptr);
        return ret;
    }
    /**
     * What year it is after the tick.
     * @returns {number}
     */
    get date_year() {
        const ret = wasm.ticksummary_date_year(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * Whether this tick crossed a day boundary.
     *
     * **The witnessing tier's clock, in one bit** (Phase 3 plan, decision 11).
     * Everything the world says about itself — what a market costs, what a town
     * has heard, who is alive to say it — is slow: the abstract tier does its
     * work on day boundaries and its heavier work on actor and entity turns
     * (`sim_core::history`), so a client that re-pulled a market every tick
     * would pull the same numbers `sim_core::calendar::TICKS_PER_DAY` times a
     * day — which Phase 5's twenty-minute day made thirty times worse.
     *
     * So the cadence is stated here rather than left for a host to guess at,
     * and it is one comparison per tick: `false` on every tick where
     * nothing dated can have changed, `true` on the one where it can. Pair it
     * with [`TickSummary::records_added`] for *"and something actually
     * happened"*.
     * @returns {boolean}
     */
    get day_changed() {
        const ret = wasm.ticksummary_day_changed(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * How many chunks changed during the tick.
     *
     * The cheap question, asked before paying for
     * [`dirty_chunks`](Self::dirty_chunks): most ticks change nothing, and a
     * client that copies an empty array sixty times a second is a client that
     * allocates sixty times a second for nothing.
     * @returns {number}
     */
    get dirty_chunk_count() {
        const ret = wasm.ticksummary_dirty_chunk_count(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * The chunks whose contents changed during the tick, as an `Int32Array` of
     * `cx, cy, z` triples (`crate::flatten`).
     *
     * The remesh driver: a chunk in here needs its mesh rebuilt, a chunk that
     * is not in here holds exactly what it held last tick. Sorted and
     * deduplicated, in `sim_core`'s chunk-coordinate order, and drained every
     * tick — what is reported is what changed *this* tick, so a host that skips
     * a summary has to rebuild its own idea of what is stale.
     *
     * **A chunk changing dirties its neighbours' meshes too, and this list does
     * not say so.** It reports changed *contents*, which is a fact about the
     * world; a mesh is culled against the 26 chunks around it (`mesher`'s
     * `slab` module), so digging a tile at a chunk edge changes what the chunk
     * next door draws without changing a single tile in it. Expanding the list
     * is the client's call because the neighbourhood is the *mesher's* radius,
     * not the simulation's, and a host that only draws a few z-levels should
     * not pay for the ones it hides.
     *
     * Expect the first tick of a session to be large: interest materialises
     * whole columns, and `sim_core`'s generated band is 88 chunks tall.
     *
     * Copies. See this type's docs.
     * @returns {Int32Array}
     */
    dirty_chunks() {
        const ret = wasm.ticksummary_dirty_chunks(this.__wbg_ptr);
        var v1 = getArrayI32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * How many tiles were dug during the tick.
     *
     * The cheap question again, and the length
     * [`dug_events`](Self::dug_events) and [`dug_materials`](Self::dug_materials)
     * agree on: `dug_events().length === 3 * dug_count` and
     * `dug_materials().length === dug_count`.
     * @returns {number}
     */
    get dug_count() {
        const ret = wasm.ticksummary_dug_count(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * Where each tile was dug, as an `Int32Array` of `x, y, z` triples
     * (`crate::flatten`).
     *
     * `sim_core::Event::Dug`, in emission order — which is tile order, not the
     * order the digs were queued in. One entry per tile *actually* dug: a dig
     * the simulation refused is not in here and is the only way a client learns
     * it was refused (see [`WasmSim::queue_dig`]).
     *
     * Paired item-for-item with [`dug_materials`](Self::dug_materials).
     * Copies. See this type's docs.
     * @returns {Int32Array}
     */
    dug_events() {
        const ret = wasm.ticksummary_dug_events(this.__wbg_ptr);
        var v1 = getArrayI32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * What each dug tile was made of, as a `Uint16Array` of material ids
     * parallel to [`dug_events`](Self::dug_events).
     *
     * The wall's material, which is also the floor's material afterwards and
     * the spoil now lying on it ([`WasmSim::items_at`]) — one dig produces one
     * of each, so the id is all three (`sim_core`'s `dig` module, DF's §11.5
     * write set).
     *
     * Copies. See this type's docs.
     * @returns {Uint16Array}
     */
    dug_materials() {
        const ret = wasm.ticksummary_dug_materials(this.__wbg_ptr);
        var v1 = getArrayU16FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 2, 2);
        return v1;
    }
    /**
     * How many records the world's history holds after the tick.
     *
     * The absolute, beside the delta, for the reason
     * [`chunk_count`](Self::chunk_count) sits beside
     * [`dirty_chunk_count`](Self::dirty_chunk_count): a client that missed a
     * summary can still tell that it is behind.
     * @returns {number}
     */
    get history_count() {
        const ret = wasm.ticksummary_history_count(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * How many records this tick appended to the world's history.
     *
     * The other half of the day boundary: a day passing is when the world
     * *may* have changed, and this is whether it did. Zero on almost every
     * tick — the reference world logs about four records a year — so a client
     * that re-reads a settlement's rumors only when this is non-zero re-reads
     * them about as often as anybody says anything.
     *
     * It counts records, not kinds: what they were is
     * [`WasmSim::describe`](crate::WasmSim::describe)'s answer, and *who heard
     * about them* is the rumor store's (`crate::flatten`).
     * @returns {number}
     */
    get records_added() {
        const ret = wasm.ticksummary_records_added(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * How many ticks the world has run for, as a JavaScript number.
     * @returns {number}
     */
    get tick() {
        const ret = wasm.ticksummary_tick(this.__wbg_ptr);
        return ret;
    }
    /**
     * How many events this adapter had no translation for.
     *
     * Always zero while `sim_core` ships one event variant. It is exported
     * anyway because the day the core grows a second one, the choice is between
     * a client that silently stops hearing about half of what happens and a
     * counter that goes up (`crate::flatten`). A host may assert on it; it is
     * not an error, it is a version skew.
     *
     * **Phase 3 wave 4A asserts it reads zero** (plan decision 11): every
     * variant the core ships is translated, and `tests/node-smoke.mjs` checks
     * it over a bootstrapped world that crosses day boundaries — which is the
     * run that would produce a new kind of event if one existed.
     * @returns {number}
     */
    get unknown_events() {
        const ret = wasm.ticksummary_unknown_events(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * The world hash after the tick: `0x` and sixteen hex digits.
     *
     * A string because a `u64` is not a JavaScript number and this one is an
     * identity rather than a quantity — the client compares it, shows it, and
     * writes it into a replay file (`web/src/world/types.ts`).
     * @returns {string}
     */
    get world_hash_hex() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.ticksummary_world_hash_hex(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
}
if (Symbol.dispose) TickSummary.prototype[Symbol.dispose] = TickSummary.prototype.free;

/**
 * A running simulation, owned by the JavaScript side.
 *
 * Created from a seed and the text of a raws file, advanced one tick at a time
 * by the host's fixed-step loop, and read between ticks. The host owns the
 * clock; this crate never looks at one (guide §8.1, §14: no `std::time` on the
 * wasm path either).
 *
 * Call `free()` when the session ends. The generated glue also registers the
 * object with `FinalizationRegistry`, so a dropped reference is collected
 * eventually — but a simulation is the largest thing on the heap and
 * "eventually" is not a budget.
 *
 * # Examples
 *
 * ```js
 * import init, { WasmSim } from "../crates/sim-wasm/pkg/web/sim_wasm.js";
 *
 * await init();
 *
 * const sim = new WasmSim(0x5eedn, await (await fetch("/materials.toml")).text());
 *
 * sim.queue_interest(0, 0, 0, 2);
 *
 * const summary = sim.tick();
 * console.log(summary.tick, summary.world_hash_hex, summary.chunk_count);
 * summary.free();
 * ```
 */
export class WasmSim {
    static __wrap(ptr) {
        const obj = Object.create(WasmSim.prototype);
        obj.__wbg_ptr = ptr;
        WasmSimFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        WasmSimFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_wasmsim_free(ptr, 0);
    }
    /**
     * Re-runs up to `max_ticks` of the loaded file and returns how many of its
     * ticks are still to run.
     *
     * **The budget is the caller's**, which is what this export exists for: a
     * browser has to paint between batches or the wait it is showing is a
     * frozen page, and how many ticks fit in a frame is a fact about the
     * machine rather than about the simulation. Zero back means the file has
     * been re-run to its last tick and the session is an ordinary one from the
     * next tick on — the queue, the recorder and the clock are the same ones
     * they always were.
     *
     * Every re-run tick goes through the same path a live one does, so the
     * recorder is fed the loaded log as it is replayed: a session that loaded a
     * file and played on exports the whole history rather than the part after
     * the load.
     *
     * A world that was not restored has nothing to advance and answers zero.
     *
     * # Errors
     *
     * Throws a JavaScript `Error` naming the tick at which the re-run stopped
     * matching the recording. The world is past that tick by then and is not
     * one anybody recorded, so the caller is expected to drop the handle; this
     * one keeps no more of the file, and a further call answers zero.
     * @param {number} max_ticks
     * @returns {number}
     */
    advance_replay(max_ticks) {
        const ret = wasm.wasmsim_advance_replay(this.__wbg_ptr, max_ticks);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0];
    }
    /**
     * Returns what is wrong with a body, as `sim_core::Afflictions`' own bit
     * mask.
     *
     * **[`WasmSim::condition`]'s companion, and the split is who is asking**
     * (`sim_core`'s `health`): a condition is the one word a stranger reads off
     * somebody walking towards them, and these are the facts a person has about
     * their own body — senseless, off their feet, empty-handed, going faint.
     * A client draws its own presentation from them and computes none of them.
     *
     * A mask rather than four calls, because a client asks it about **one**
     * figure — its own — every frame or so, and because the bits are
     * append-only like every other id here: one this build's client does not
     * know is one a later wave minted, and it reads as an affliction with no
     * treatment rather than as a wrong answer.
     *
     * Zero for everybody nothing has happened to, and for a handle this world
     * has never had.
     * @param {number} figure
     * @returns {number}
     */
    afflictions(figure) {
        const ret = wasm.wasmsim_afflictions(this.__wbg_ptr, figure);
        return ret >>> 0;
    }
    /**
     * Returns whom a figure is about to swing at, or -1 if nobody.
     *
     * **[`WasmSim::fleeing_from`]'s twin** (`sim_core`'s `combat`): the answer
     * pass asks three questions — a fresh hand, still standing here, and a
     * Bravery band — and the pair covers both ways that band can go. Derived,
     * stored nowhere.
     *
     * **This is the read that tells a host when to start speaking.** The
     * simulation knows where the player is standing only because a host said
     * so ([`WasmSim::queue_stand`]), and the honest moment to start saying it
     * is the pulse before somebody swings — which is this. Watch it beside
     * [`WasmSim::engaged_with`] and both ways a body ends up striking the
     * player are covered: answering a blow, and a watchman's arrest.
     *
     * -1 for everybody who is not answering anybody, which is everybody in an
     * ordinary town, and for a handle this world has never had.
     * @param {number} figure
     * @returns {number}
     */
    answering(figure) {
        const ret = wasm.wasmsim_answering(this.__wbg_ptr, figure);
        return ret;
    }
    /**
     * Returns the record of the last time the watch took a figure, or -1.
     *
     * **[`WasmSim::death_of`]'s twin one record over** (`sim_core`'s
     * `Snapshot::arrest_of`, chunk Fx-5), and it is forwarded for that read's
     * exact reason: hand the id to [`WasmSim::notice`] and the town's own board
     * says who was taken and what for, so a client that reports what happened
     * while somebody was out cold reports the *log's* sentence and writes none
     * of it.
     *
     * Two things about the answer are the core's and are worth knowing on this
     * side: it is found by the **culprit** and never by the hand that made the
     * arrest, and it is the **last** taking rather than the first, because
     * unlike a death an arrest can happen more than once to one person.
     *
     * A record id, so it is an `f64` for [`WasmSim::death_of`]'s reason. -1 is
     * the sentinel and means *never taken*, which is nearly everybody.
     * @param {number} figure
     * @returns {number}
     */
    arrest_of(figure) {
        const ret = wasm.wasmsim_arrest_of(this.__wbg_ptr, figure);
        return ret;
    }
    /**
     * Returns how many arrows the player has left.
     *
     * Half of what a client needs to explain a refused shot, and the half a
     * quiver line is drawn from. Zero in every session nobody has fletched
     * anything in, which is most of them.
     * @returns {number}
     */
    arrows() {
        const ret = wasm.wasmsim_arrows(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * Returns what a settlement can show over the next `window_days`, as
     * `id, surface, subject, at_year, at_day, cited…` rows
     * (`crate::flatten`).
     *
     * **The spotlight feed** (Phase 4 plan, decision 1):
     * [`spotlight`](sim_core::Snapshot::spotlight), flattened. What is worth
     * showing is decided in `sim_core`; the client's director consumes this
     * plus the two inputs the simulation can never hold — where the player's
     * body is, and what their journal already has — and times **presentation
     * only**. It may delay and space; it may never fabricate, reorder causality
     * or surface a beat before the date in its row.
     *
     * # Why the window only reaches forwards
     *
     * `window_days` is a horizon rather than a radius, and the difference is
     * smaller than it looks: material that *stands* — a grave, a ruin, the
     * price currently on the board, a rumor a town already holds — never fades,
     * so it is offered by a window starting today however long ago it landed.
     * What a backwards window would add is material that has already been and
     * gone — last month's caravan, a column that arrived three weeks ago — and
     * staging a reaction to those is staging a reaction to something that is no
     * longer there to meet.
     *
     * Empty for a settlement this world does not have.
     * @param {number} settlement
     * @param {number} window_days
     * @returns {Float64Array}
     */
    beats(settlement, window_days) {
        const ret = wasm.wasmsim_beats(this.__wbg_ptr, settlement, window_days);
        var v1 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
        return v1;
    }
    /**
     * Returns how many years of history the world was constructed on top of.
     *
     * What the constructor was given, not a measurement of what came out of it:
     * how many settlements those years founded and how many wars they fought is
     * the log's business ([`WasmSim::history_count`]).
     * @returns {number}
     */
    bootstrap_years() {
        const ret = wasm.wasmsim_bootstrap_years(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * [`born_orphan_house_name`](Self::born_orphan_house_name) for the bound
     * child's door.
     *
     * Usually the empty string: a child the chronicles wrote down generally
     * carries the line they were born into, and the nonce reaches only a name
     * the world is *coining*.
     * @param {string} name
     * @param {number} figure
     * @param {number} coin_nonce
     * @returns {string}
     */
    born_bound_house_name(name, figure, coin_nonce) {
        let deferred2_0;
        let deferred2_1;
        try {
            const ptr0 = passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len0 = WASM_VECTOR_LEN;
            const ret = wasm.wasmsim_born_bound_house_name(this.__wbg_ptr, ptr0, len0, figure, coin_nonce);
            deferred2_0 = ret[0];
            deferred2_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred2_0, deferred2_1, 1);
        }
    }
    /**
     * Returns the family name a birth would coin at one nonce, or the empty
     * string when that birth coins none.
     *
     * **The *coin another* button's read** (the surname ruling, owner
     * 2026-08-25). The ruling gives the player a reroll rather than a text
     * field, and choosing between draws means seeing them: this is what the
     * nonce on the next press would produce, answered by
     * `sim_core::Sim::coined_house_name` — which gets it right by running the
     * birth door on a throwaway copy of the world rather than by predicting it.
     *
     * **Nothing about this world moves.** No command is queued, no tick runs
     * and no hash changes; it is a read like every other export in this module,
     * and the one thing that separates it is what it costs (a clone of the
     * world, which pre-spawn is the history tier and nothing else — see the
     * core's own note).
     *
     * The empty string is the answer for every birth that **inherits** — a
     * bound child of a standing line, an unrecorded child of housed parents —
     * because no press can rename a house, and for every birth this world
     * refuses. A client that shows the button only where this answers with
     * something is reading the ruling's own rule off the simulation instead of
     * transcribing it.
     *
     * Three doors, three methods, exactly as
     * [`queue_born_orphan`](crate::WasmSim::queue_born_orphan) and its two
     * siblings are three: the arguments are the door's, and a payload with a
     * `kind` in it is a payload a client can get wrong while every number stays
     * plausible.
     * @param {string} name
     * @param {number} settlement
     * @param {number} coin_nonce
     * @returns {string}
     */
    born_orphan_house_name(name, settlement, coin_nonce) {
        let deferred2_0;
        let deferred2_1;
        try {
            const ptr0 = passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len0 = WASM_VECTOR_LEN;
            const ret = wasm.wasmsim_born_orphan_house_name(this.__wbg_ptr, ptr0, len0, settlement, coin_nonce);
            deferred2_0 = ret[0];
            deferred2_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred2_0, deferred2_1, 1);
        }
    }
    /**
     * [`born_orphan_house_name`](Self::born_orphan_house_name) for the
     * unrecorded child's door.
     *
     * It answers with something only when **both parents are houseless**: a
     * child of a line inherits it, which is the ruling's other half read off
     * the simulation rather than transcribed here.
     * @param {string} name
     * @param {number} mother
     * @param {number} father
     * @param {number} coin_nonce
     * @returns {string}
     */
    born_unrecorded_house_name(name, mother, father, coin_nonce) {
        let deferred2_0;
        let deferred2_1;
        try {
            const ptr0 = passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len0 = WASM_VECTOR_LEN;
            const ret = wasm.wasmsim_born_unrecorded_house_name(this.__wbg_ptr, ptr0, len0, mother, father, coin_nonce);
            deferred2_0 = ret[0];
            deferred2_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred2_0, deferred2_1, 1);
        }
    }
    /**
     * Returns the display number of the bow the player would loose with, or
     * zero for a pair of hands with no bow in them.
     *
     * The other half, and a **handle rather than a flag** so that a surface can
     * name it — *"your oak bow"* through [`item_label`](Self::item_label) —
     * which is decision 11's split kept: the simulation owns the words. Zero is
     * the honest nothing on [`settlement_name`](Self::settlement_name)'s terms;
     * no item ever has display number zero, because arena slots are allocated
     * from one.
     *
     * The pair with [`arrows`](Self::arrows) is what lets a client say **which**
     * of *no bow* and *no arrows* refused the press, rather than shrugging.
     * @returns {number}
     */
    bow() {
        const ret = wasm.wasmsim_bow(this.__wbg_ptr);
        return ret;
    }
    /**
     * Returns what the player is carrying as `item, kind, material, quality`
     * rows (`crate::flatten`).
     *
     * The inventory's read half. Empty in every world nobody has claimed a
     * figure in, and — the ordinary case — for a player who has picked nothing
     * up.
     *
     * What each item is *called* crosses per call
     * ([`item_label`](Self::item_label)), which is decision 11's split: the
     * three ids on the row are what a client groups, keys and counts by, and the
     * words are the simulation's.
     *
     * Bounded by `sim_core::MAX_CARRIED`, forwarded as
     * [`max_carried`](crate::max_carried) so a client can size a panel before it
     * has one.
     * @returns {Float64Array}
     */
    carried() {
        const ret = wasm.wasmsim_carried(this.__wbg_ptr);
        var v1 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
        return v1;
    }
    /**
     * Returns the records one book cites, in the order it cites them.
     *
     * **The half of a book a journal keeps.** A journal entry is a reference
     * and its provenance and never a copy of what was said (Phase 3 plan,
     * decision 2), so reading a chronicle has to hand back ids as well as
     * sentences: the line is what the player read and the
     * [`EventId`] is what they now hold — traceable in a debug view, comparable
     * against a price, a grave and a mouth carrying the same record.
     *
     * That makes a book a witness channel like any other, which is what it
     * already is on the simulation's side: a settlement's own shelves teach it
     * entries off these same ids, one a month
     * (`sim_core::history::chronicle`'s entity turn). A client filing them is
     * doing what the town does.
     *
     * **Parallel to [`chronicle_lines`](Self::chronicle_lines)**: same order,
     * same length, so `entries[i]` is the record `lines[i]` voices. Two calls
     * rather than one pairing, because the two halves cross on different sides
     * of decision 11's split — numeric bulk flat, strings per call — and a
     * caller that only wants to *file* a book it has already read should not
     * re-render every page to do it.
     *
     * Empty for a handle this world does not have.
     * @param {number} chronicle
     * @returns {Float64Array}
     */
    chronicle_entries(chronicle) {
        const ret = wasm.wasmsim_chronicle_entries(this.__wbg_ptr, chronicle);
        var v1 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
        return v1;
    }
    /**
     * Returns what one book says, one string per record it cites.
     *
     * The **written** register over the book's own citations
     * (`sim_core::Snapshot::chronicle_lines`): no contractions, the year spelled
     * out, and the ground under it the town the book stands in — the same voice
     * a market board posts in ([`notice`](Self::notice)) and deliberately not
     * the voice of a mouth. A page is read, not heard.
     *
     * Strings per call, which is decision 11's rule and the reason the pages
     * are not columns in [`chronicles`](Self::chronicles): a book is opened on
     * an interaction, at most
     * [`max_chronicle_entries`](crate::max_chronicle_entries) lines of it, and
     * a client that walked a town's shelves would otherwise be marshalling
     * every page of every book to draw a prop.
     *
     * The whole book in one call rather than a line at a time, because a book
     * is what the player opened: the alternative is a count and an index, which
     * is two exports and a loop across the boundary to assemble a thing the
     * simulation already hands over whole.
     *
     * Empty for a handle this world does not have.
     *
     * Page `i` is about record `i` of
     * [`chronicle_entries`](Self::chronicle_entries): same order, same length.
     * @param {number} chronicle
     * @returns {string[]}
     */
    chronicle_lines(chronicle) {
        const ret = wasm.wasmsim_chronicle_lines(this.__wbg_ptr, chronicle);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]);
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Returns the books standing in a settlement as
     * `id, author, settlement, x, y, z, left_year, left_day, entries` rows
     * (`crate::flatten`).
     *
     * **A chronicle is sim state; the prose is not** (Phase 5 plan, decision
     * 5). The row says where a book is and who wrote it; what it *says* is
     * [`chronicle_lines`](Self::chronicle_lines), composed by `sim_core` out of
     * the records it cites, and *which* records those are is
     * [`chronicle_entries`](Self::chronicle_entries) — so the only thing a
     * player can put in the world's mouth is which records it should read
     * out.
     *
     * Asked per settlement rather than of the world, like every other read
     * about where the player is standing: the filter is the simulation's own
     * (`sim_core::history::Chronicles::at`), and a book belongs to a town by
     * construction — one left further from every town than a book's reach is
     * not left at all.
     *
     * The author is a [`FigureId`] and may be somebody long dead: the parity
     * half of this verb is a victorious ruler leaving an account of their war
     * (`player-as-figure.md` §2), so a world nobody has ever played in still
     * has books in it. Their name crosses per call like every other name.
     *
     * Empty for a settlement this world does not have, and for a town whose
     * shelves are bare — which is most of them.
     * @param {number} settlement
     * @returns {Float64Array}
     */
    chronicles(settlement) {
        const ret = wasm.wasmsim_chronicles(this.__wbg_ptr, settlement);
        var v1 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
        return v1;
    }
    /**
     * Returns how many chunks the world has generated.
     * @returns {number}
     */
    chunk_count() {
        const ret = wasm.wasmsim_chunk_count(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * Returns the seats a figure's parents held, as
     * `parent, settlement, position, cites` rows (`crate::flatten`).
     *
     * [`origin_claims`](Self::origin_claims) asked about somebody who already
     * exists — a player who came through the birth door, and equally any figure
     * at all, because a claim is a fact about parentage and the projection has
     * no idea who is playing (`sim_core::Snapshot::claims`). That is the parity
     * half of it: the same read answers about a ruler's son nobody is playing.
     *
     * It is what a client asks *after* the birth, where
     * [`origin_claims`](Self::origin_claims) is what it asks before: the card
     * is gone once the command lands, and the figure is what is left.
     *
     * At most [`max_claims`](crate::max_claims) rows. Empty for a figure this
     * world does not have, for one the world remembers no parent of — every
     * founder, every champion from outside — and for a player who came through
     * the claim door.
     * @param {number} figure
     * @returns {Float64Array}
     */
    claims(figure) {
        const ret = wasm.wasmsim_claims(this.__wbg_ptr, figure);
        var v1 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
        return v1;
    }
    /**
     * Returns, for each chunk column asked about, the highest level it will
     * ever hold anything at — a flat `Int32Array`, in the order asked.
     *
     * `sim_core::Snapshot::column_reach`, unchanged, and **the top of a host's
     * mesh band** (wave 1.6, item 10). A client that draws terrain has to pick
     * how far up a column to look; the wasm layer refuses to narrow the z band
     * for it — *"a host that only draws a few z-levels should not pay for the
     * ones it hides"* ([`TickSummary::dirty_chunks`](crate::TickSummary::dirty_chunks))
     * — so the narrowing is the host's arithmetic and this is the one input to
     * it that only the simulation knows. Above this level the column is sky and
     * stays sky.
     *
     * **Chunk-column coordinates, not tiles.** The argument is flat `cx, cy`
     * pairs, which is what `TickSummary::dirty_chunks` reports and what
     * [`Command::Interest`](crate::WasmSim::queue_interest) is expressed in,
     * because a mesh band is chosen per chunk column — and because a maximum
     * over the whole 16×16 footprint is the only form of the answer that is
     * safe: a host sampling five tiles of the sixteen misses the gable between
     * two probes, which is exactly the defect this export was added for.
     *
     * **Asked in a batch**, on `WasmWitnessSource::open_ground`'s terms and for
     * its reason: laying a town out is the expensive half of the plan and
     * reading a column of it is the cheap one, so a client that wants the
     * square its window covers asks for the square. Measured on seed 0x5 with
     * no ground materialised — which is exactly when a client first asks —
     * eighty-one columns cost **2,413 ms one at a time and 45 ms together**;
     * once generation has folded the plans, 9 ms.
     *
     * An odd-length argument is truncated to whole columns rather than refused:
     * there is no error channel across this wall, and half a coordinate names
     * nothing (`WasmSim::queue_interest`'s own stance on host input).
     *
     * **It never rises**, so a host may memoise it per column for the life of a
     * session: the town is read at the last stage its plan has, a felled tree
     * lowers the world and a dig only opens it. What that costs is a few levels
     * of empty air over a settlement that has not grown into its plan yet, and
     * air meshes to nothing.
     *
     * Like [`ground_level`](crate::WasmSim::ground_level) it reads no map, so
     * it answers for chunk columns no interest has generated — which is the
     * point: a band decides what to ask for before the asking.
     * @param {Int32Array} columns
     * @returns {Int32Array}
     */
    column_reach(columns) {
        const ptr0 = passArray32ToWasm0(columns, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmsim_column_reach(this.__wbg_ptr, ptr0, len0);
        var v2 = getArrayI32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v2;
    }
    /**
     * Returns everybody who follows a figure, as their display numbers.
     *
     * [`leader_of`](Self::leader_of)'s other direction, bounded by
     * [`max_companions`](crate::max_companions) — so a host may ask it about
     * its own figure every frame the way it asks what is in its hands. Empty
     * for everybody nobody follows, which is nearly everybody.
     * @param {number} figure
     * @returns {Int32Array}
     */
    companions(figure) {
        const ret = wasm.wasmsim_companions(this.__wbg_ptr, figure);
        var v1 = getArrayI32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Returns how hurt a figure is, as [`Condition::id`](sim_core::Condition).
     *
     * **A word's worth of state and never a number** (Phase 7's cross-cutting
     * rule: *"combat consequence never lives host-side … any damage number
     * computed in the client is a bug by definition"*). The simulation keeps
     * three accumulators per tissue layer of every wound and §6.1b's own
     * instruction is what crosses instead — *model richly, summarise brutally*
     * — so a client gets one of four ids and looks the word up
     * ([`WasmSim::condition_word`]).
     *
     * Zero — unhurt — for everybody nothing has happened to, which is almost
     * everybody forever, and for a handle this world has never had. It answers
     * for a figure with **no body**, on [`WasmSim::life_stage`]'s terms: a
     * wound is a fact about a person rather than about a town being watched.
     *
     * One figure at a time, for [`WasmSim::is_adult`]'s stated reason: a debug
     * window asks it once a second over a roster of ten, and nothing on a
     * per-frame path asks it at all.
     * @param {number} figure
     * @returns {number}
     */
    condition(figure) {
        const ret = wasm.wasmsim_condition(this.__wbg_ptr, figure);
        return ret;
    }
    /**
     * Returns the word for a condition id, or `""` for a body with nothing
     * wrong with it.
     *
     * The vocabulary read, forwarded rather than copied: the words are
     * `sim_core`'s and a client that kept its own table would be a second place
     * for *badly hurt* to be decided (`crate::WasmSim`'s forwarding rule, and
     * [`life_stage_word`](crate::life_stage_word)'s precedent).
     *
     * `""` for [`Condition::Unhurt`](sim_core::Condition) — the sim
     * answers `None` there and the empty string is how a `None` crosses a wall
     * that carries strings — and for an id this build does not ship.
     * @param {number} condition
     * @returns {string}
     */
    condition_word(condition) {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.wasmsim_condition_word(this.__wbg_ptr, condition);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Returns the word for a crime id, or `""` for an id this build does not
     * ship.
     *
     * [`WasmSim::condition_word`]'s twin, forwarded for its reason: the legal
     * code is `sim_core`'s table (`sim_core`'s `watch`, history §7.5) and a
     * client that kept its own copy would be a second place for *theft* to be
     * decided.
     * @param {number} crime
     * @returns {string}
     */
    crime_name(crime) {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.wasmsim_crime_name(this.__wbg_ptr, crime);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Returns what a town would hold against the player for swinging at
     * somebody, as a crime id, or -1 for nothing.
     *
     * [`crime_of_taking`](Self::crime_of_taking)'s twin one verb over, and it
     * answers about **the act**: the id that comes back is the mildest row a
     * landed blow writes, because the graver two are what the blow turns out to
     * *do* and nothing on this side of the wall may compute that (the phase's
     * rule — a damage number in a client is a bug by definition).
     *
     * -1 before a claim, for a watchman already engaged with the person he
     * would be swinging at, for a body the world cannot place, and for a handle
     * this world has never had.
     * @param {number} figure
     * @returns {number}
     */
    crime_of_striking(figure) {
        const ret = wasm.wasmsim_crime_of_striking(this.__wbg_ptr, figure);
        return ret;
    }
    /**
     * Returns what taking whatever is on a tile would put on the player's
     * blotter, as `sim_core::history::CrimeKind`'s own id, or -1 for nothing.
     *
     * `Command::Take`'s own pick and its own two clauses, run without the
     * writes: **carrying off somebody you are no kin to** is abduction and
     * **walking off with what is somebody else's** is theft, with the body
     * picked before the item because that is the order the verb picks in.
     *
     * **Asking is not deciding** ([`work_at`](Self::work_at)'s rule): the tile
     * is re-read at the tick the command lands on, and nothing here promises
     * the press will do anything at all.
     *
     * -1 before a claim, for a tile with nothing on it a pair of hands would
     * lift, for what nobody owns, for your own things, and for kin.
     * @param {number} x
     * @param {number} y
     * @param {number} z
     * @returns {number}
     */
    crime_of_taking(x, y, z) {
        const ret = wasm.wasmsim_crime_of_taking(this.__wbg_ptr, x, y, z);
        return ret;
    }
    /**
     * Returns what day of the year it is, counting from zero.
     * @returns {number}
     */
    date_day_of_year() {
        const ret = wasm.wasmsim_date_day_of_year(this.__wbg_ptr);
        return ret;
    }
    /**
     * Returns what year it is in the world.
     *
     * The calendar `sim_core` records history in
     * (`sim_core::calendar::WorldDate`), split into its two components because
     * that is what the client's contract states and because comparing two dates
     * is comparing the two numbers in order — no arithmetic, no knowledge of
     * how long a year is (`web/src/world/types.ts`'s `WorldDateView`).
     *
     * Read at a day boundary rather than per frame:
     * [`TickSummary::day_changed`] is what says one has passed.
     * @returns {number}
     */
    date_year() {
        const ret = wasm.wasmsim_date_year(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * Returns the record that says a figure died, or -1 while they live.
     *
     * **The read permadeath is built on** (`sim_core`'s `Snapshot::death_of`,
     * and the phase's decision 7). A client watching its own figure learns that
     * it is over from the *log*, and what it shows next is the log's own
     * words — hand the id straight to [`WasmSim::notice`] and the killer, the
     * weapon and the cause come back as a sentence the simulation composed.
     * Nothing about how somebody died is written by a renderer.
     *
     * A record id, so it is an `f64` on this boundary for
     * [`WasmSim::queue_tell`]'s reason: a sequence number is wider than the
     * `i32` the handle reads use and narrower than 53 bits, so a double carries
     * it exactly. -1 is the sentinel, and it means *alive* or *never existed* —
     * which are the same answer for the same reason: there is nothing written
     * down.
     * @param {number} figure
     * @returns {number}
     */
    death_of(figure) {
        const ret = wasm.wasmsim_death_of(this.__wbg_ptr, figure);
        return ret;
    }
    /**
     * Returns how many uncovered ticks demote a settlement.
     *
     * The simulation's own hysteresis, forwarded rather than transcribed, for
     * [`walk_ticks_per_step`](crate::walk_ticks_per_step)'s reason: a client
     * that wrote the number down would be keeping a second copy of something
     * the calendar derives, and it would go stale the day the calendar is
     * retuned.
     *
     * It is the **edge-walker's** number since item 6 rather than the standing
     * player's: what demotes a town is a new interest that no longer covers it,
     * and this is how long the world waits before believing that a player who
     * has stepped over the halo's edge means to leave.
     *
     * A method rather than a free function because `sim_core` publishes it
     * through a snapshot and this crate holds the handle a snapshot comes from.
     * @returns {number}
     */
    demotion_ticks() {
        const ret = wasm.wasmsim_demotion_ticks(this.__wbg_ptr);
        return ret;
    }
    /**
     * Returns the log's own sentence for a record, with its handles named.
     *
     * **The debug half of the traceability, and never diegetic text.** A journal
     * entry holds an [`EventId`]; this is how a debug view turns it back into
     * the line a chronicler would write — the same sentence `sim-cli legends`
     * prints, because it is the same projection (`HistoryEvent::narrate`
     * rewritten through [`Names`](sim_core::history::Names)). What a *person*
     * would say about it is [`speak`](Self::speak)'s answer and reads nothing
     * like it.
     *
     * **A player-facing surface must not call this**, and until wave 4 of the
     * voice overhaul one did: the market board's cause line was this string,
     * handle numbers and casualty subtraction and parenthetical date included
     * (`docs/plans/speech-overhaul-plan.md`, "one register leak found"). The
     * board asks [`notice`](Self::notice) now, and the callers left here are the
     * trace view and the journal's per-record narration — both debug surfaces,
     * both behind a key the player has to know about.
     *
     * The date is appended rather than left to the caller: `narrate` carries no
     * date on purpose — *"the date belongs to the sort, and the projection is
     * the sentence"* — and a debug line that has to be assembled from two calls
     * is a debug line somebody assembles differently the second time. It goes
     * *inside* the sentence: `narrate` ends on a full stop, so the stop is
     * lifted and put back after the date rather than leaving the line trailing
     * a second, orphaned clause after it has already ended.
     * @param {number} event
     * @returns {string}
     */
    describe(event) {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.wasmsim_describe(this.__wbg_ptr, event);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Returns who a figure is taken up with, or -1 if nobody.
     *
     * **The engagement floor across the wall** (`sim_core`'s `local`, and the
     * phase plan's rider 3): while this answers, neither body is walking and
     * neither is being sent anywhere, so a client may draw the two of them
     * standing together and know the simulation agrees. Today the only thing
     * that engages anybody is a watchman making an arrest (`sim_core`'s
     * `watch`); when the conversation area lands, a talk is the same state and
     * needs no second export.
     *
     * -1 for everybody standing about, which is nearly everybody, and for a
     * handle this world has never had.
     * @param {number} figure
     * @returns {number}
     */
    engaged_with(figure) {
        const ret = wasm.wasmsim_engaged_with(this.__wbg_ptr, figure);
        return ret;
    }
    /**
     * Returns what each of an age's lines narrates, as `slot, cites, event…`
     * per line (`crate::flatten`).
     *
     * **The trace half of the pair** (Phase 6 plan, decision O6), and the reason
     * the saga is checkable rather than merely asserted: every line carries at
     * least one [`EventId`] that resolves in the log, so a debug view can put
     * the record behind a sentence in front of the owner and *"the saga narrates
     * records that exist, never invents"* is a thing a test performs.
     *
     * A line narrates one record or two, and the second one is the interesting
     * one: a declaration of war narrated *"for the tribute it would not pay"* is
     * a sentence about **two** records, and a trace that showed only the war
     * would hide the half that makes it a sentence about cause. So the count is
     * on the row rather than the width being fixed — `crate::flatten` argues the
     * shape, which is the one place on this boundary that differs from a beat's.
     *
     * `slot` is `sim_core::history::saga::Slot`'s own discriminant — opener 0,
     * body 1, close 2 — and it is what a client lays a chapter out by. The ids
     * are append-only and nothing hashes them, so an unknown one is a slot this
     * build did not ship rather than an error.
     *
     * **Parallel to [`era_lines`](Self::era_lines)**: same order, same number of
     * groups. Empty for a row this book does not have.
     * @param {number} era
     * @returns {Float64Array}
     */
    era_citations(era) {
        const ret = wasm.wasmsim_era_citations(this.__wbg_ptr, era);
        var v1 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
        return v1;
    }
    /**
     * Returns one age's chapter, one string per line, in reading order.
     *
     * The opener that narrates the record the age turns on, a line per record
     * the age is remembered for, and the close — `sim_core`'s own slot chain
     * (`saga::Slot`), composed in the one register in this build that is
     * *written* rather than said. The lines cross **whole**, like every other
     * string on this surface: a client that assembled a chapter out of a place
     * and a year would be authoring the world's fiction in JavaScript, which is
     * the failure devlog #2 exists to name.
     *
     * **Parallel to [`era_citations`](Self::era_citations)**: line `i` here is
     * narrated by citation group `i` there — same order, same count. That
     * pairing is decision O6's trace contract and the reason a saga is allowed
     * to read like a saga at all.
     *
     * The whole chapter in one call rather than a line at a time, exactly as
     * [`chronicle_lines`](Self::chronicle_lines) hands over a whole book: the
     * alternative is a count and an index, which is two exports and a loop
     * across the boundary to reassemble something the simulation already has
     * whole.
     *
     * Empty for a row this book does not have.
     * @param {number} era
     * @returns {string[]}
     */
    era_lines(era) {
        const ret = wasm.wasmsim_era_lines(this.__wbg_ptr, era);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]);
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Returns what a book calls an age by what happened in it — *the years
     * after the fall of Barfell* — or the empty string.
     *
     * [`era_title`](Self::era_title)'s other half of the same taste call, with
     * the same bounds and the same empty answer.
     * @param {number} era
     * @returns {string}
     */
    era_name(era) {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.wasmsim_era_name(this.__wbg_ptr, era);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Returns what a book calls an age by its number — *the second age* — or
     * the empty string.
     *
     * **One of two surfaces on an open taste call** (Phase 6 plan, open
     * questions: *"do eras get generated names … or ordinals"*). Both are built
     * sim-side and both cross, so the owner picks one at the read-through and no
     * prose is rewritten either way; [`era_name`](Self::era_name) is the other.
     * A client showing both until then is showing the question.
     *
     * `era` is a row index into [`saga_eras`](Self::saga_eras) — see this
     * section's header for why it is not the ordinal — and one past the end is
     * the empty string rather than an exception, for the reason
     * [`settlement_name`](Self::settlement_name) answers the same way: walking
     * off a list is a normal thing for a client to do, and it is not an error.
     * @param {number} era
     * @returns {string}
     */
    era_title(era) {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.wasmsim_era_title(this.__wbg_ptr, era);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Returns the session so far as the text of a replay file.
     *
     * The format `sim-cli` reads: `(seed, command log, hash sequence)` as
     * JSON, version 5 (`sim_replay`). Recording is always on and costs a `u64`
     * per tick (`crate::record`), so this is a read rather than the end of
     * something — a host may export mid-session and keep playing, and the next
     * export describes the longer run.
     *
     * What a host does with the string is the host's business: this crate has
     * no filesystem and does not want one (guide §8.1). The browser client
     * hands it to a download; the round trip that matters is
     * `cargo run -p sim-cli -- verify <file>`, which re-runs the commands
     * against a fresh world and compares every hash.
     *
     * # Errors
     *
     * Throws a JavaScript `Error` if a command submitted during the session
     * has no encoding in the current replay format — which means `sim_core`
     * grew a verb `sim_replay`'s mirror has not been taught, and the file
     * would describe a run nobody performed (`crate::record`).
     * @returns {string}
     */
    export_replay() {
        let deferred2_0;
        let deferred2_1;
        try {
            const ret = wasm.wasmsim_export_replay(this.__wbg_ptr);
            var ptr1 = ret[0];
            var len1 = ret[1];
            if (ret[3]) {
                ptr1 = 0; len1 = 0;
                throw takeFromExternrefTable0(ret[2]);
            }
            deferred2_0 = ptr1;
            deferred2_1 = len1;
            return getStringFromWasm0(ptr1, len1);
        } finally {
            wasm.__wbindgen_free(deferred2_0, deferred2_1, 1);
        }
    }
    /**
     * Returns the marks the log has left within `radius` tiles of a column, as
     * `kind, x, y, event, figure` rows (`crate::flatten`).
     *
     * **Realised on approach** (Phase 3 plan, decision 6, world-gen §10.5): a
     * client asks when the player nears somewhere and renders what comes back
     * as props. Nothing is stored, nothing is hashed, nothing blocks a tile, and
     * two builds that place a grave differently place a *prop* differently —
     * the world hash cannot see this call.
     *
     * The answer is in the log's own order, so it does not depend on how the
     * question was asked.
     * @param {number} x
     * @param {number} y
     * @param {number} radius
     * @returns {Float64Array}
     */
    features(x, y, radius) {
        const ret = wasm.wasmsim_features(this.__wbg_ptr, x, y, radius);
        var v1 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
        return v1;
    }
    /**
     * Returns the byname a figure's own records earned them, or the empty
     * string.
     *
     * The epithet projection (wave 3A): *"the Twice-Besieged"* is filtered,
     * sorted and projected out of the log exactly like every other viewer's
     * answer, so it is `None` for most people and free to ask for
     * (`sim_core::history::byname`).
     * @param {number} figure
     * @returns {string}
     */
    figure_byname(figure) {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.wasmsim_figure_byname(this.__wbg_ptr, figure);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Returns what a figure is called, or the empty string for a handle this
     * world does not have.
     * @param {number} figure
     * @returns {string}
     */
    figure_name(figure) {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.wasmsim_figure_name(this.__wbg_ptr, figure);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Returns which settlement a figure belongs to, or `-1`.
     *
     * The one link a client needs that the roster does not carry: the roster is
     * asked *about* a settlement, and this is the way back for a figure a
     * client got from somewhere else — a grave's citation, a battle's champion.
     * @param {number} figure
     * @returns {number}
     */
    figure_settlement(figure) {
        const ret = wasm.wasmsim_figure_settlement(this.__wbg_ptr, figure);
        return ret;
    }
    /**
     * Returns whose hand a figure is running from, or -1 if they are not
     * running.
     *
     * **Derived, not stored** (`sim_core`'s `combat`): a figure is fleeing
     * while they carry a fresh wound from somebody still standing here and
     * their own Bravery band says they run. The client draws the same
     * derivation the simulation walks on, so a row that says *fleeing* and a
     * body that is walking away are one answer rather than two.
     *
     * -1 for everybody who is not running, which is everybody in an ordinary
     * town, and for a handle this world has never had.
     * @param {number} figure
     * @returns {number}
     */
    fleeing_from(figure) {
        const ret = wasm.wasmsim_fleeing_from(this.__wbg_ptr, figure);
        return ret;
    }
    /**
     * Returns the groups of people on the road into a settlement, as
     * `event, from, to, people, departed_year, departed_day` rows
     * (`crate::flatten`).
     *
     * **A flight is a battle read back, not a second kind of state.** Every
     * column comes off the `BattleFought` record itself — who was sacked, which
     * road led somewhere safe, how many walked out and on what day
     * (`sim_core::history::war`'s `flee`) — and the group is found through the
     * settlement's *own rumor store*, because a refugee delivering their news to
     * the refuge is how the store learned about that battle in the first place
     * (Phase 3 plan, decision 4). The people walking up the road and the entry
     * that says the town knows are the same record, which is exactly the claim
     * the phase's exit criterion makes.
     *
     * `within_days` is the caller's: how long a group is still *on the road* is
     * a presentation question — the walk is drawn client-side and the
     * simulation never learns where anybody is standing (decision 8) — so this
     * crate takes the window rather than choosing one.
     * @param {number} settlement
     * @param {number} within_days
     * @returns {Float64Array}
     */
    flights(settlement, within_days) {
        const ret = wasm.wasmsim_flights(this.__wbg_ptr, settlement, within_days);
        var v1 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
        return v1;
    }
    /**
     * Returns what a figure would say if they offered to follow the player, or
     * `""` if they would not.
     *
     * **One read rather than a flag and a line**, which is the point: a client
     * that could draw the prompt without the sentence would be a client with an
     * opinion about who offers. The rule is `sim_core::companions`' and the
     * words are assembled the way every other spoken line in this build is,
     * over a record the town has actually heard about the hero — so two
     * neighbours who admire the same person say it differently.
     *
     * It asks **no distance**: where the player is standing is a sentence the
     * host says, and a read that demanded one would have a client streaming its
     * position all session to find out whether to draw a prompt. The reach is
     * measured at [`queue_beckon`](WasmSim::queue_beckon) instead.
     *
     * `""` in every world nobody has claimed a figure in, and for everybody who
     * does not qualify — which is nearly everybody, in nearly every town.
     * @param {number} figure
     * @returns {string}
     */
    follow_offer(figure) {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.wasmsim_follow_offer(this.__wbg_ptr, figure);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Returns the z-level of the walkable surface at a column.
     *
     * `sim_core::Snapshot::ground_level`, unchanged. It re-derives the
     * elevation field from `(seed, x, y)` instead of reading the map, so it
     * answers for **every** column in the world, including ones no interest has
     * generated yet — which is what a client needs it for: choosing where to
     * put the player before asking for the terrain under them.
     *
     * The answer is always inside [`world_z_min`](crate::world_z_min)`..=`
     * [`world_z_max`](crate::world_z_max), and once the column exists, the tile
     * at this level is the walkable floor with open space above it.
     * @param {number} x
     * @param {number} y
     * @returns {number}
     */
    ground_level(x, y) {
        const ret = wasm.wasmsim_ground_level(this.__wbg_ptr, x, y);
        return ret;
    }
    /**
     * Returns `true` if a shelf is standing on a tile.
     *
     * [`shelved`](Self::shelved)'s companion and the reason it can afford to
     * answer *empty* twice: a library has a shelf from the first day somebody
     * stands in the town, and whether anything is on it is the other question.
     * @param {number} x
     * @param {number} y
     * @param {number} z
     * @returns {boolean}
     */
    has_shelf(x, y, z) {
        const ret = wasm.wasmsim_has_shelf(this.__wbg_ptr, x, y, z);
        return ret !== 0;
    }
    /**
     * Returns how many records the world's history holds.
     *
     * The cheap question a client asks to find out whether anything it has
     * pulled is stale — the same shape as
     * [`TickSummary::dirty_chunk_count`], one tier up. It saturates rather
     * than wrapping and cannot: the reference world logs about four records a
     * year (`sim-cli legends`), so four billion of them is a run no clock
     * reaches.
     * @returns {number}
     */
    history_count() {
        const ret = wasm.wasmsim_history_count(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * Returns what a line of descent is called, or the empty string for a
     * handle this world does not have.
     *
     * [`settlement_name`](Self::settlement_name)'s counterpart for the third
     * arena, and it arrives with the birth door because that is the first
     * surface with a house handle on it: a card's `house` column is the line a
     * life is born into ([`origins`](Self::origins)), and *of the line of X* is
     * what the screen has to be able to say.
     *
     * Resolved through `sim_core::Snapshot::names`, which is the same table
     * every narrated sentence in the world looks a house up in — so the name a
     * card shows is the name the log uses for it.
     *
     * The empty string for a card whose `house` is `-1`, which is a life with
     * **nothing to inherit** rather than a lookup that failed: the birth door
     * coins a name for it when the command lands (Phase 6 plan, decision 5).
     * @param {number} house
     * @returns {string}
     */
    house_name(house) {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.wasmsim_house_name(this.__wbg_ptr, house);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Returns what a grave says, or the empty string.
     *
     * items §10.4's slab: **a figure reference and a date, never authored
     * text**. The stone is composed by `sim_core` out of the record and the
     * figure it names ([`speech::inscription`]) and crosses whole, because a
     * client that assembled a sentence out of a name and two numbers would be
     * authoring content in JavaScript — the failure devlog #2 exists to name.
     *
     * A feature is identified the way it is derived: by the record it cites and
     * its kind, which is the pair [`aftermath::near`] itself de-duplicates on.
     * Nothing else about a feature is stable, because nothing about it is
     * stored.
     *
     * Empty for a feature that is not a grave — a battlefield marker says
     * nothing, because nobody wrote on it — and for a record this world does not
     * have.
     * @param {number} event
     * @param {number} kind
     * @returns {string}
     */
    inscription(event, kind) {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.wasmsim_inscription(this.__wbg_ptr, event, kind);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Returns how a figure introduces themselves, or the empty string.
     *
     * **A figure with no news must not be silent** (Phase 4 plan, decision
     * 10b): [`speech::introduce`] over the figure's own record — their name,
     * the byname their own records earned them, where they are from, and one
     * facet-coloured clause. The same template discipline as
     * [`speech::line`] and no authored claim in it.
     *
     * A string rather than a [`Spoken`], because there is no record to cite:
     * an introduction is about the speaker and puts nothing in the journal.
     * That is also why it is not a fallback inside [`speak`](Self::speak) —
     * *"they had nothing to tell me, so they told me who they were"* is a
     * different thing to show, and which one to show is the client's
     * presentation call.
     * @param {number} figure
     * @returns {string}
     */
    introduction(figure) {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.wasmsim_introduction(this.__wbg_ptr, figure);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Returns whether a figure has come of age and is alive.
     *
     * **The read the debug window's population line splits on** (item 6's rider
     * 2, ruled 2026-08-28: *children get bodies, or they do not exist on the
     * street*). Presence stopped being an adults-only roster when the sim half
     * of this chunk widened `residents` — the streets hold children now — so a
     * client that says *"ten alive here"* can say how many of them are grown
     * without deriving an age from a birth date, which would be the client
     * keeping a copy of a stage table it does not own (`sim_core`'s
     * `Snapshot::is_adult`, and its `Figure::is_actor`).
     *
     * `false` for a figure this world has never had, and for one who is dead —
     * which are the same answer for the same reason every other read here gives
     * it: the honest thing to say about a handle that names nobody.
     *
     * One figure at a time, and that is deliberate rather than a gap: the caller
     * asks it once a second over a roster of ten, and a flattened *"who here is
     * grown"* array would be a second layout to keep in step with the roster's
     * for a question nothing on a per-frame path asks.
     * @param {number} figure
     * @returns {boolean}
     */
    is_adult(figure) {
        const ret = wasm.wasmsim_is_adult(this.__wbg_ptr, figure);
        return ret !== 0;
    }
    /**
     * Returns `true` if a walker standing at the first tile could reach the
     * second.
     *
     * `sim_core::Snapshot::is_connected`: an integer comparison of the two
     * tiles' walk-group labels rather than a search
     * (`docs/research/df-systems-map/systems/pathfinding-and-jobs.md` §8.3), so
     * a client may ask it about every candidate in a list before paying for a
     * path to any of them. `false` when either tile is not walkable, including
     * one in a chunk that has not been generated.
     *
     * Six coordinates rather than two position objects: a wasm-bindgen struct
     * argument would allocate on the JavaScript side per call, and this is a
     * question a client asks in a loop.
     *
     * Terrain only. Per-creature permissions — a flier, a climber, a door
     * somebody locked — are filters a caller applies *after* this answers, and
     * never changes to the labels (map-and-blocks.md §7.1).
     * @param {number} ax
     * @param {number} ay
     * @param {number} az
     * @param {number} bx
     * @param {number} by
     * @param {number} bz
     * @returns {boolean}
     */
    is_connected(ax, ay, az, bx, by, bz) {
        const ret = wasm.wasmsim_is_connected(this.__wbg_ptr, ax, ay, az, bx, by, bz);
        return ret !== 0;
    }
    /**
     * Returns `true` when the world has generated the tile at all.
     *
     * The query that separates "air" from "not here yet". A client that must
     * not fall through the world while chunks stream in builds its own
     * unknown-is-solid rule out of this and [`is_solid`](Self::is_solid); the
     * simulation does not have that rule, because it is a rule about a
     * renderer (`crate::terrain`).
     * @param {number} x
     * @param {number} y
     * @param {number} z
     * @returns {boolean}
     */
    is_generated(x, y, z) {
        const ret = wasm.wasmsim_is_generated(this.__wbg_ptr, x, y, z);
        return ret !== 0;
    }
    /**
     * Returns `true` when the tile blocks a body from occupying it.
     *
     * DF's `passable_flow` attribute, negated (`crate::terrain`). Coordinates
     * are integer tile coordinates; a client with a float transform floors
     * before asking. A tile the world has not generated is **not** solid — see
     * [`is_generated`](Self::is_generated).
     * @param {number} x
     * @param {number} y
     * @param {number} z
     * @returns {boolean}
     */
    is_solid(x, y, z) {
        const ret = wasm.wasmsim_is_solid(this.__wbg_ptr, x, y, z);
        return ret !== 0;
    }
    /**
     * Returns `true` when a creature can stand on the tile.
     *
     * DF's `walkable` attribute, "can be stood upon" (`crate::terrain`). A tile
     * the world has not generated is not walkable.
     * @param {number} x
     * @param {number} y
     * @param {number} z
     * @returns {boolean}
     */
    is_walkable(x, y, z) {
        const ret = wasm.wasmsim_is_walkable(this.__wbg_ptr, x, y, z);
        return ret !== 0;
    }
    /**
     * Returns `true` if a figure is one of the promoted town's watch.
     *
     * **The captain, or somebody the board has on a patrol** (`sim_core`'s
     * `watch`), which is the predicate the answering pass asks before it lets
     * anybody take hold of a culprit. A client reads it so that the moment it
     * starts telling the simulation where the player is standing
     * ([`queue_stand`](Self::queue_stand)) is a moment the simulation would
     * send somebody — a wanted player with a watchman beside them is a player
     * about to be arrested, and until the tile is stated there is nothing for
     * the hand to reach.
     *
     * `false` in a world with nothing promoted, which is most of them, and for
     * a handle this world has never had.
     * @param {number} figure
     * @returns {boolean}
     */
    is_watchman(figure) {
        const ret = wasm.wasmsim_is_watchman(this.__wbg_ptr, figure);
        return ret !== 0;
    }
    /**
     * Returns what one item is called, or the empty string for a handle the
     * world no longer holds.
     *
     * **Three tables the simulation owns, composed sim-side and crossing
     * whole** — the rung, the material and the noun
     * (`sim_core::Snapshot::item_label`). A client that assembled *"masterwork
     * oak table"* out of three ids would be keeping copies of three
     * vocabularies, and one of them is the raws (devlog #2's rule, and the
     * reason a market board stopped saying `settlement #12`).
     *
     * The argument is a display number off a
     * [`carried`](Self::carried) or [`tile_items`](Self::tile_items) row, and it
     * is resolved against the arena **as it is now** — items are the one family
     * of handle this simulation removes, so a number a client filed away last
     * minute may name nothing or name something else. The empty string is the
     * honest answer to both, on
     * [`settlement_name`](Self::settlement_name)'s terms: asking about
     * something that is not there is a normal thing for a client walking a list
     * to do.
     * @param {number} item
     * @returns {string}
     */
    item_label(item) {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.wasmsim_item_label(this.__wbg_ptr, item);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Returns what the world *calls* one item, or the empty string for
     * anything it has no name for.
     *
     * **The promoted tier's read** (`sim_core::Snapshot::item_name`, Phase 7's
     * decision 3): the empty string for every ordinary boulder, blade and meal
     * in the world, and a coined name for the handful of things something
     * record-worthy has touched — a blade that has killed somebody the world
     * remembers, a masterwork off a bench.
     *
     * It is a **second call beside [`item_label`](Self::item_label)** rather
     * than a widening of it, and the split is the same one decision 11 draws
     * everywhere else: the label is what a thing *is* — three words off three
     * tables, identical for every iron blade in the town — and this is what one
     * particular thing is *called*. A client that wants *"Karfang, an iron
     * blade"* asks both and writes the comma, which is composition rather than
     * a second vocabulary crossing the wall.
     *
     * Resolved against the arena as it is now, on [`item_label`](Self::item_label)'s
     * terms — except that this one cannot go stale in the way that one can: a
     * named item is never removed (`sim_core::Items::remove` refuses), so a
     * number that resolved to a name last minute resolves to the same name now.
     * @param {number} item
     * @returns {string}
     */
    item_name(item) {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.wasmsim_item_name(this.__wbg_ptr, item);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Returns the material ids of the items lying on a tile, in handle order.
     *
     * What a client draws on a dug floor. Empty for a tile nothing was dropped
     * on, which is almost every tile — the cheap way to use it is to ask about
     * the tiles [`TickSummary::dug_events`] just named, not to scan.
     *
     * One `Uint16Array` of material ids (`crate::flatten`), indexing the same
     * table as [`TickSummary::dug_materials`] and as the mesher's colours.
     *
     * **The shape survived Phase 7's wave 1A and the meaning narrowed**: the
     * spoil is real items in an arena now rather than materials keyed by a
     * tile, so this array is what those items are *made of* and no longer all
     * there is to know about them. The export a client will want when it grows
     * an inventory is the handles themselves; it is not here because nothing
     * consumes it yet (wave 1F).
     * @param {number} x
     * @param {number} y
     * @param {number} z
     * @returns {Uint16Array}
     */
    items_at(x, y, z) {
        const ret = wasm.wasmsim_items_at(this.__wbg_ptr, x, y, z);
        var v1 = getArrayU16FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 2, 2);
        return v1;
    }
    /**
     * Returns the promoted town's board as `kind, x, y, z, worker` rows
     * (`crate::flatten`).
     *
     * **The recordless layer's data** (plan, O11 and the 1F brief): a town's
     * daily work is *seen, never logged*, so a client that wants to know what
     * people are doing asks this and never the chronicle. What it gets is the
     * same board the town's own bidders take work off, in the order the world
     * hash folds it.
     *
     * Nothing here decides a scene. Which prop goes at a site, what a worker's
     * arms do and how fast they do it are rendering choices, exactly as which
     * way a gravestone faces is; what crosses is what kind of work it is and
     * whose.
     * @returns {Int32Array}
     */
    jobs_at() {
        const ret = wasm.wasmsim_jobs_at(this.__wbg_ptr);
        var v1 = getArrayI32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Returns whom a figure follows, as their display number, or -1 for
     * nobody.
     *
     * **The companionship, read** (`sim_core`'s `companions`, decision 10). A
     * handle rather than a word, so a client compares it against its own player
     * figure and never learns a rule: *following you* and *following Halvard
     * Reedmere* are the same read with a different answer.
     *
     * It is also the read that tells a host to keep saying where the player is
     * standing ([`WasmSim::queue_stand`], as wave 2F widened the rule): a
     * companion walks after the tile the host states, because the player has no
     * body for the simulation to walk after.
     *
     * -1 for everybody in an ordinary town, and for a handle this world has
     * never had.
     * @param {number} figure
     * @returns {number}
     */
    leader_of(figure) {
        const ret = wasm.wasmsim_leader_of(this.__wbg_ptr, figure);
        return ret;
    }
    /**
     * Returns which life stage a figure is in, or -1 for one this world has
     * never had.
     *
     * [`WasmSim::is_adult`]'s wave-2A widening, and the read the debug
     * window's population line splits on: *"ten alive here"* now says how many
     * are grown, how many are children, how many are toddlers and how many are
     * babies (rider 2's filed gap, *"the debug window's alive-here splits by
     * stage"*).
     *
     * **Derived sim-side and never stored** (`sim_core::body`): a pure function
     * of a birth date, the clock and the race's stage table, so there is
     * nothing here for a client to cache and a client that worked an age out
     * from a birth date would be keeping a copy of a table it does not own.
     *
     * It answers for a figure with **no body**, which is what makes it the
     * right read for a roster line — a stage is a fact about a person rather
     * than about a town being watched. A *drawn* body's stage rides its
     * presence row instead ([`WasmSim::present_figures`]), because that one is
     * asked per frame.
     *
     * One figure at a time, for [`WasmSim::is_adult`]'s stated reason.
     * @param {number} figure
     * @returns {number}
     */
    life_stage(figure) {
        const ret = wasm.wasmsim_life_stage(this.__wbg_ptr, figure);
        return ret;
    }
    /**
     * Returns how many debug notes the local tier has ever made.
     *
     * Monotonic for the life of a world, notes the ring has since dropped
     * included. A client keeps the number it last saw, hands it to
     * [`local_notes_since`](Self::local_notes_since), and compares: fewer lines
     * than the difference means the ring outran it, which is what a month
     * crossed in one `run_days` looks like from the reading end.
     * @returns {number}
     */
    local_notes_made() {
        const ret = wasm.wasmsim_local_notes_made(this.__wbg_ptr);
        return ret;
    }
    /**
     * Returns every debug note made after `made`, oldest first, one line each.
     *
     * Empty is the ordinary answer — an ordinary second of an ordinary session
     * makes no notes at all — and empty is also what a `made` this boundary
     * cannot read exactly answers with, on
     * `crate::convert`'s `sequence_from_f64` rule: a sequence number that is
     * not a sequence number was never one.
     * @param {number} made
     * @returns {string[]}
     */
    local_notes_since(made) {
        const ret = wasm.wasmsim_local_notes_since(this.__wbg_ptr, made);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]);
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Returns a settlement's standing buildings as
     * `kind, trade, door_x, door_y, work_x, work_y` rows (`crate::flatten`).
     *
     * **The workshop verb's read** (`sim_core::LotPost`): what a building is,
     * where somebody stands outside it, and where the bench inside it is — which
     * is the same tile [`queue_craft`](crate::WasmSim::queue_craft) names, so
     * the offer a client draws and the command it sends are one column of one
     * row.
     *
     * A pure projection over the town plan, which is itself a deterministic
     * function of (seed, settlement records) — so this is derived on the call
     * and stored nowhere (`worldgen-direction.md` §4). Asked per settlement for
     * [`chronicles`](Self::chronicles)' reason: which town a client is drawing
     * is a fact the client has and the projection does not.
     *
     * The `trade` column is the workshop verb's **gate**, and it is decided
     * sim-side: it is the job kind practised at that bench, or `-1` for a
     * building nobody works in. A client that offered the verb at every
     * doorstep would be offering a press the simulation refuses in silence,
     * which is a verb that looks broken (`sim_core::LotPost`).
     *
     * The kind ids are `sim_core::LotKind`'s own and are append-only, so one
     * this build does not know is a building a later one added — to be drawn
     * plainly rather than dropped. `web/src/world/types.ts` checks its copies
     * against [`lot_kind_ids`] at startup rather than trusting them.
     * @param {number} settlement
     * @returns {Int32Array}
     */
    lots(settlement) {
        const ret = wasm.wasmsim_lots(this.__wbg_ptr, settlement);
        var v1 = getArrayI32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Returns a settlement's market, one row per stock (`crate::flatten`).
     *
     * **Prices are derived and never stored** (Phase 3 plan, decision 7), so
     * this is computed on the call: the index and the band off the settlement's
     * ledger, and the latest [`PriceShock`](sim_core::history::PriceShock) about
     * that stock off the settlement's own log index. The five shock columns are
     * what makes a market board a *channel* — `cause` is the battle that
     * emptied the granary, and it is the same [`EventId`] a rumor two towns away
     * carries.
     *
     * Every commodity the build ships, in id order, so a panel walks the array
     * rather than asking which stocks exist.
     *
     * The `cause` column is a handle, and what the *board* says about it is
     * [`notice`](Self::notice) — a string, pulled per row when the panel is
     * open, because that is which side of decision 11's split prose is on. It
     * takes this same `settlement` back, because a board speaks with the ground
     * under it and the town whose market this is is the town the board is in.
     * @param {number} settlement
     * @returns {Float64Array}
     */
    market(settlement) {
        const ret = wasm.wasmsim_market(this.__wbg_ptr, settlement);
        var v1 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
        return v1;
    }
    /**
     * Returns every material this world's raws name, in id order.
     *
     * **The one table on this boundary that is a fact about the *world* rather
     * than about the build**, which is why it is a method and not a free
     * function beside [`item_kind_ids`]: materials are parsed out of the raws
     * text the host handed over at construction (`sim_core::materials`), so two
     * sims can disagree about them and a caller has to ask the one it holds.
     *
     * The index *is* the id — a `MaterialId` is a position in this list — so
     * nothing else has to cross for a caller to name one. That is the same
     * arrangement `sim_core` itself uses, and it is why this is one call rather
     * than a pair of them.
     *
     * Exported for the developer's give door (chunk Fx-3, `web/src/dev/give.ts`)
     * and for nothing else so far: everything a *player* ever reads about a
     * material is already inside a composed label
     * ([`item_label`](Self::item_label)), which is decision 11's split doing its
     * job — the client never assembles *"an iron blade"* itself.
     * @returns {string[]}
     */
    material_names() {
        const ret = wasm.wasmsim_material_names(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]);
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Returns whether one material is a solid, by its index into
     * [`material_names`](Self::material_names).
     *
     * **The raws' own `solid` column**, published rather than transcribed for
     * [`item_portable`]'s reason: it is a fact about a row
     * of a table this client does not own, and a client-side list of which
     * materials are rock would be a second opinion nobody keeps in step.
     *
     * The one caller so far is the developer's give door, which opens on the
     * first solid row of the table (chunk Fx-3, `web/src/dev/give.ts`): a blade
     * made of air is a legal item and a useless one to fight with, and *which
     * materials are air* is the world's answer.
     *
     * `false` for an index this world has no row for, which is the safe
     * direction as well as the honest one.
     * @param {number} material
     * @returns {boolean}
     */
    material_solid(material) {
        const ret = wasm.wasmsim_material_solid(this.__wbg_ptr, material);
        return ret !== 0;
    }
    /**
     * Meshes one chunk and hands back its vertex buffers.
     *
     * `cx`/`cy` are chunk indices and `z` is a tile z-level, because a chunk is
     * one tile thick and dividing z would be a lie
     * (`sim_core::map::ChunkCoord`).
     *
     * A chunk the world has not generated meshes to an empty [`ChunkMesh`]
     * rather than to an error, and so does a chunk of pure air or one sealed
     * inside rock. That falls out of the mesher's contract rather than being
     * special-cased here: an ungenerated tile "culls nothing and occludes
     * nothing" (`mesher`'s `slab` module), and a slab with no visible faces has
     * no triangles.
     *
     * Cost is the mesher's few microseconds plus the 972 map lookups the
     * padded neighbourhood needs, and the lookups are the larger half:
     * `tests/node-smoke.mjs` measures about 0.1 ms per chunk in Node,
     * including copying the four buffers out. The mesher reads the chunk *and*
     * its 26 neighbours, which is also why editing one tile dirties up to 26
     * chunk meshes — the number whoever owns the dirty set has to budget for.
     * @param {number} cx
     * @param {number} cy
     * @param {number} z
     * @returns {ChunkMesh}
     */
    mesh_chunk(cx, cy, z) {
        const ret = wasm.wasmsim_mesh_chunk(this.__wbg_ptr, cx, cy, z);
        return ChunkMesh.__wrap(ret);
    }
    /**
     * Creates a simulation from a seed, the text of a raws file, and the years
     * of history to stand it on.
     *
     * The seed is a `BigInt` on the JavaScript side: it is a full `u64` and a
     * JavaScript number is not. The raws are text because `sim-core` reads no
     * files — the host fetches `data/materials.toml` and hands over what it
     * got (guide §8.1).
     *
     * # The bootstrap
     *
     * `bootstrap_years` is [`Sim::with_bootstrap`]'s argument and Phase 3
     * plan decision 1's whole point: a browser at 30 Hz spends twenty
     * wall-minutes on an in-game day (`sim_core::calendar`, since Phase 5's
     * retune) and can never reach the first founding, so
     * a world worth witnessing has to arrive already lived in. Construction
     * day-steps through those years — foundings, dynasties, caravans, wars —
     * and the world this returns is the world that many ordinary ticks would
     * have produced, hash included.
     *
     * Omitting it (or passing `undefined`) is a world with no past, which is
     * exactly what every session before Phase 3 was and what
     * [`Sim::new`](sim_core::Sim::new) still means. It is optional rather than
     * required because "no history" is a real answer a host may want — the dig
     * prototype wants it — and not a default this crate invented.
     *
     * The number is recorded in the session's replay (`crate::record`), so
     * `sim-cli verify` rebuilds the world the session played in rather than a
     * fresh one.
     *
     * # Errors
     *
     * Throws a JavaScript `Error` carrying the parse failure's message when the
     * raws are malformed, empty, or name a material twice. Domain errors do not
     * cross layers unchanged (guide §8.2): `sim_core::raws::RawsError` becomes
     * an exception here, and the exception's message is the error's `Display`,
     * which already names the offending material and index.
     * @param {bigint} seed
     * @param {string} raws_toml
     * @param {number | null} [bootstrap_years]
     */
    constructor(seed, raws_toml, bootstrap_years) {
        const ptr0 = passStringToWasm0(raws_toml, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmsim_new(seed, ptr0, len0, isLikeNone(bootstrap_years) ? Number.MAX_SAFE_INTEGER : (bootstrap_years) >>> 0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        WasmSimFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Returns what a market board posts about the record that moved a price, or
     * the empty string.
     *
     * **[`describe`](Self::describe)'s diegetic opposite, and the reason it has
     * one.** A market row carries the `PriceShock`'s `cause` — the battle that
     * emptied the granary — and the board explaining *why* the price moved is
     * what makes the market one of the phase's three channels rather than a
     * gauge (Phase 3 plan, decision 7). That explanation used to be the dev
     * chronicle, so a player reading a board in a town met `settlement #12` and
     * a casualty subtraction; this is the same record in the world's own
     * register (`sim_core::history::speech::notice`,
     * `docs/plans/speech-overhaul-plan.md`, wave 4).
     *
     * The line crosses **whole**, like every other string on this surface
     * (decision 11): `sim_core` composes it out of the record and the world's
     * names, and a client that assembled a notice out of a place and a year
     * would be authoring the world's fiction in JavaScript.
     *
     * It takes the *cause* record's id, not the shock's — the caller has both on
     * the market row ([`market`](Self::market)'s layout,
     * `crate::flatten`) — because a board that narrated the swing would be
     * explaining a price with the price.
     *
     * # Which board is asking
     *
     * `settlement` is the town the board stands in, and it is the same number
     * the caller passed [`market`](Self::market) to get the row this cause came
     * off. A board belongs to a town, so the projection reads it as the ground
     * under the notice: a record about that town posts *"this town was stormed
     * in year 169"*, and the next town's board keeps naming it
     * (`sim_core::history::speech::notice`, the owner's ruling on wave 4's first
     * open question).
     *
     * The consequence for a caller: **the same record may read differently on
     * two towns' boards**, so a client must not cache a notice by `event` alone.
     * The pair is the identity.
     *
     * The empty string for a record *or* a settlement this world does not have,
     * for the reason [`settlement_name`](Self::settlement_name) answers the same
     * way: a stale id in a panel is a normal thing for a client to hold, and it
     * is not an error.
     * @param {number} settlement
     * @param {number} event
     * @returns {string}
     */
    notice(settlement, event) {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.wasmsim_notice(this.__wbg_ptr, settlement, event);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Returns `true` if a figure would follow the player.
     *
     * The rule on its own, for the surface that wants the *verb* without the
     * sentence: an interact prompt is decided every frame and a line is a
     * string every time it is asked for.
     * @param {number} figure
     * @returns {boolean}
     */
    offers_to_follow(figure) {
        const ret = wasm.wasmsim_offers_to_follow(this.__wbg_ptr, figure);
        return ret !== 0;
    }
    /**
     * Returns, for each `x, y` column asked about, the nearest one a
     * settlement's plan does not speak for and nothing is growing on, as
     * `found, x, y` rows (`crate::flatten`).
     *
     * **The prop-placement read** (`sim_core::Snapshot::open_ground_near`, wave
     * 1.5's graybox ruling — *nothing stands in a street*). A client places
     * chronicle stands, market boards and graves from a *record's* position,
     * and a record knows nothing about the town that grew around it: the wave-1
     * playtest found them standing in roads and inside building footprints.
     * This is what a client asks so a prop lands beside the road instead.
     *
     * The judgement is the simulation's on [`lots`](Self::lots)' terms — what
     * the plan claims, what is growing there, and which candidate is nearest
     * are all facts the plan and the fields hold and the client does not. **It
     * is a pure function of (seed, records)**, which is what makes a prop stay
     * where it was put as the world streams in around it; how high the ground
     * is stays [`ground_level`](crate::WasmSim::ground_level)'s answer.
     *
     * **Asked in a batch, and it matters here more than anywhere else on this
     * boundary**: laying a town out is the expensive half of the plan, so a
     * client with a dozen props to place derives one plan and not a dozen.
     * One row per column asked about, in order; `found` is `0` where there is
     * no open ground within the plan's own search radius, and a client that
     * gets one leaves that prop where the record put it.
     *
     * An odd-length argument is truncated to whole columns rather than refused:
     * there is no error channel across this wall, and half a coordinate names
     * nothing (`WasmSim::queue_interest`'s own stance on host input).
     * @param {number} settlement
     * @param {Int32Array} columns
     * @returns {Int32Array}
     */
    open_ground(settlement, columns) {
        const ptr0 = passArray32ToWasm0(columns, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmsim_open_ground(this.__wbg_ptr, settlement, ptr0, len0);
        var v2 = getArrayI32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v2;
    }
    /**
     * Returns the seats one card's parents held, as
     * `parent, settlement, position, cites` rows (`crate::flatten`).
     *
     * **The claims a life is born with** (Phase 6 cut list: claims ship as a
     * read-only projection this phase, and nothing presses one). *Child of a
     * figure who held the seat of X* — which is the sentence the noble half of
     * the birth screen exists to be able to draw, and the reason the unrecorded
     * and bound doors rank the most storied parents first.
     *
     * `origin` is the **row index** into [`origins`](Self::origins), counting
     * from zero — see this section's header — and the count in that row's last
     * column is how many rows come back here. One past the end is empty rather
     * than an exception, the wall's convention since
     * [`settlement_name`](Self::settlement_name).
     *
     * Empty for a life whose parents held nothing, which is most of them.
     * @param {number} origin
     * @returns {Float64Array}
     */
    origin_claims(origin) {
        const ret = wasm.wasmsim_origin_claims(this.__wbg_ptr, origin);
        var v1 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
        return v1;
    }
    /**
     * Returns the lives a player could be born into, as
     * `kind, settlement, cites, mother, father, figure, house, claims` rows
     * (`crate::flatten`), in reading order.
     *
     * **The origins screen** (Phase 6 plan, decision 3 — *B-lite*, and
     * `docs/research/player-fantasies/fantasy-01-born-into-the-world.md` §2):
     * three doors into the record — the orphan of a town an army came through,
     * a child no chronicle recorded, and a child the chronicles already wrote
     * down — each carrying the record behind it, the parents it names, the line
     * it is born into and how many seats those parents held.
     *
     * `kind` is which door ([`origin_kind_ids`], [`origin_kind_name`]) and it is
     * what a client switches on to choose which of the three
     * [`queue_born_orphan`](Self::queue_born_orphan),
     * [`queue_born_unrecorded`](Self::queue_born_unrecorded) and
     * [`queue_born_bound`](Self::queue_born_bound) to hand the row's handles
     * back to. `cites` resolves in the log like every other id on this surface
     * — the sack for an orphan, the birth for a child — so a card can be read
     * out ([`describe`](Self::describe)) rather than described by the client.
     *
     * At most [`max_origins`](crate::max_origins) rows. **Empty is a real
     * answer**: a world with nobody in it yet, and one with no ruined town, no
     * pair of parents old enough and no recorded child young enough. A session
     * that finds it falls back to the claim door
     * ([`queue_claim`](Self::queue_claim)), which needs only a town.
     * @returns {Float64Array}
     */
    origins() {
        const ret = wasm.wasmsim_origins(this.__wbg_ptr);
        var v1 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
        return v1;
    }
    /**
     * Returns what a follower says when they will come no further, or `""`
     * while they are still coming.
     *
     * **The halo, spoken** (`sim_core`'s `companions`): v1 companions follow
     * within promoted ground, and the predicate behind this is the town's own
     * coverage test — so a companion comes exactly as far as the simulated
     * ground does, and the client says so rather than deciding it.
     *
     * `""` for everybody who follows nobody and for every follower whose leader
     * is still on the town's ground, which is the ordinary case and is why a
     * client may poll it.
     * @param {number} figure
     * @returns {string}
     */
    parting_line(figure) {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.wasmsim_parting_line(this.__wbg_ptr, figure);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Returns the figure the player's deeds cite, or `-1`.
     *
     * **The whole of the player in the simulation** (Phase 5 plan, decision 1,
     * `docs/plans/player-as-figure.md` §1): a handle in the log's social graph,
     * put there by [`queue_claim`](Self::queue_claim) and written to by nothing
     * else. Every other question about the player is a question about that
     * figure, asked with the exports that were already here —
     * [`figure_name`](Self::figure_name),
     * [`figure_byname`](Self::figure_byname) as their deeds earn them one,
     * [`figure_settlement`](Self::figure_settlement).
     *
     * `-1` in every world nobody has claimed a name in, which is every headless
     * run, every golden, and every session up to the moment the claim lands. It
     * is the same spelling of *"nobody"*
     * [`figure_settlement`](Self::figure_settlement) uses, because it is the
     * same kind of answer.
     *
     * This is also how a client learns whether a claim it queued was accepted:
     * a tick has no error channel, so the honest test is to ask afterwards
     * ([`queue_claim`](Self::queue_claim) lists the three silent refusals).
     * @returns {number}
     */
    player_figure() {
        const ret = wasm.wasmsim_player_figure(this.__wbg_ptr);
        return ret;
    }
    /**
     * Returns every body in the promoted town as
     * `figure, x, y, z, stage, held_by` rows (`crate::flatten`).
     *
     * **Sim positions, where sim positions exist.** Phase 3's decision 8 said
     * the simulation never learns where anybody is standing and wave 1F amended
     * exactly that clause for exactly one settlement: promotion synthesises the
     * named roster onto real ground and moves them along real paths (plan, O2
     * and O7).
     *
     * **One owner per body** (item 6's rider 1, ruled 2026-08-28). Wave 1F left
     * the client drawing its own ambient crowd for the towns nobody had
     * promoted, which meant one roster with two owners and a swap between them
     * whenever a town promoted or demoted — the teleport the wave-1.6
     * investigation could not pin from a screenshot. A named figure is drawn
     * from *this list* or is not drawn at all now; the client's loop keeps its
     * future as the unnamed crowd layer and draws no figures
     * (`web/src/witness/crowd.ts`).
     *
     * **The player is not in here**, and that is the local tier's own ruling
     * rather than a filter applied at this boundary (wave 1B's calls: *"the
     * player's figure never gets a presence record"*). The body is still the
     * host's.
     *
     * **The stage and the carrier ride the position** since wave 2A, because a
     * renderer needs all three to draw one body: how tall to draw it
     * ([`body_scale_permille`](crate::body_scale_permille)), how fast to glide
     * it ([`walk_ticks_per_step_for_stage`](crate::walk_ticks_per_step_for_stage))
     * and whose arms to draw it in. A body somebody is carrying is drawn at its
     * carrier and the simulation's tile for it is the tile it was lifted from —
     * see the sim-side note, which is where that asymmetry is argued.
     *
     * At most the settlement's *named* people: the anonymous population is a
     * count and has never been anywhere (history §14.1).
     * @returns {Int32Array}
     */
    present_figures() {
        const ret = wasm.wasmsim_present_figures(this.__wbg_ptr);
        var v1 = getArrayI32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Returns the promoted settlement's handle, or `-1` when nothing is
     * promoted.
     *
     * **The local tier's one question** (Phase 7 plan, O2). A settlement
     * promotes when a [`queue_interest`](Self::queue_interest) covers its
     * ground and the halo around it, so the answer is world state, is hashed,
     * and is the same for two hosts replaying one command log.
     *
     * **It is also the client's `here()`** (item 6's rider 4, ruled
     * 2026-08-28). There used to be a second answer beside it — the client's own
     * *nearest town at any distance* — and the window in which the two
     * disagreed is where every symptom the item-6 investigation chased fired.
     * The client forwards to this now (`web/src/world/wasm.ts`), so *which town
     * am I in* has one answer and *nowhere* is one of the things it can say.
     *
     * `-1` is [`player_figure`](Self::player_figure)'s spelling of *nobody* and
     * means the same thing: a world nobody is standing in.
     * @returns {number}
     */
    promoted_settlement() {
        const ret = wasm.wasmsim_promoted_settlement(this.__wbg_ptr);
        return ret;
    }
    /**
     * Returns the caravans on a settlement's roads as
     * `id, from, to, day_year, day_of_year, talker` rows (`crate::flatten`).
     *
     * **A caravan on screen is a pulse the simulation computed** (Phase 4 plan,
     * decision 2): origin, destination and day are sim truth and the client
     * picks nothing but how to show them. The list is the current entity turn's
     * and is replaced every turn including by an empty one
     * ([`Pulses`](sim_core::history::Pulses)), so *"no caravan left this month"*
     * is an answer rather than stale data.
     *
     * Both ends of a road, because a pulse is a round trip: a town sees the
     * column that left it as well as the one walking in.
     *
     * `within_days` is the caller's, exactly as it is for
     * [`flights`](Self::flights) — how long a column counts as still being on
     * the road is a presentation question and the simulation never learns where
     * anybody is standing (Phase 3 plan, decision 8). Every pulse of a turn
     * shares that turn's day, so the window is all-or-nothing rather than a
     * filter, which is what makes it *"is the last shipment still walking"*.
     * @param {number} settlement
     * @param {number} within_days
     * @returns {Float64Array}
     */
    pulses(settlement, within_days) {
        const ret = wasm.wasmsim_pulses(this.__wbg_ptr, settlement, within_days);
        var v1 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
        return v1;
    }
    /**
     * Queues taking somebody along.
     *
     * Becomes `sim_core::Command::Beckon`, and it cites a **person** — the
     * third command to do it, and the same exception
     * [`queue_strike`](Self::queue_strike) argues rather than a new one.
     *
     * **It confirms an offer the world made.** A figure offers to follow a hero
     * whose deeds have reached them and whose dials lean that way
     * ([`WasmSim::follow_offer`]), so this is the player saying yes rather than
     * asking — the owner's UI rule, kept where the press is.
     *
     * Send [`queue_stand`](Self::queue_stand) immediately before it, exactly as
     * for a swing and for the same reason: the reach is measured from the tile
     * the simulation is holding, and a beckon from a tile it never heard of is
     * a defect.
     *
     * Ignored, silently, for six reasons and every one of them is a rule a
     * client can apply first: no player figure, a number this world has never
     * had, a figure with no body in the promoted town, no `stand` ever sent,
     * the two tiles further apart than
     * [`beckon_reach_tiles`](crate::beckon_reach_tiles), and anything the offer
     * rule itself refuses.
     * @param {number} figure
     */
    queue_beckon(figure) {
        wasm.wasmsim_queue_beckon(this.__wbg_ptr, figure);
    }
    /**
     * Queues a birth that binds the player to a child the world already wrote
     * down.
     *
     * Becomes `sim_core::Command::Born` with `Birth::Bound`, and it is the door
     * that **creates nothing**: the figure existed before the session started,
     * and their birth record, their scheduled death, their facets and their
     * house are the ones the world gave them years ago. The one write it makes
     * is the name — binding names them (§2) — which is why a client that offers
     * this door still asks the player to type one.
     *
     * `figure` is the display number in the `figure` column of an
     * [`origins`](Self::origins) card whose `kind` is the bound child's. That
     * column is `-1` on the other two doors, because their person does not
     * exist until the command is submitted.
     *
     * `coin_nonce` may be passed and will usually do nothing: a bound child
     * generally carries the line they were born into, and the nonce only
     * reaches a name the world is *coining* — which here is the case of a
     * child whose parents had no house either (`sim_core`'s `history::house`).
     *
     * **Ignored, silently**, when the name normalizes to nothing, when a player
     * figure already exists, and when the figure is not somebody this world
     * will hand over: dead, a child, seated, too old, or nobody's recorded
     * child. The screen's own list is the honest source, and the door re-checks
     * every rule behind it (`sim_core::history::origins`'s `resolve`).
     * @param {string} name
     * @param {number} figure
     * @param {number | null} [coin_nonce]
     */
    queue_born_bound(name, figure, coin_nonce) {
        const ptr0 = passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        wasm.wasmsim_queue_born_bound(this.__wbg_ptr, ptr0, len0, figure, isLikeNone(coin_nonce) ? Number.MAX_SAFE_INTEGER : (coin_nonce) >>> 0);
    }
    /**
     * Queues a birth as somebody of the late player's own line.
     *
     * Becomes `sim_core::Command::Born` with the heir selector — **the fourth
     * door, and the one permadeath opens** (Phase 7 plan, decision 7). Like the
     * bound door it creates nothing: the kinsman existed before the session
     * started, and binding names them.
     *
     * `figure` is the display number in the `figure` column of an
     * [`origins`](Self::origins) card whose `kind` is the heir's. That door is
     * **empty in every session the player is still alive in**, which is what
     * makes it a door rather than a feature.
     *
     * **An heir may be a child**, and that is rider 2's ruling 1 rather than an
     * oversight: what a played nine-year-old may then do is the stage table's
     * business — no work but chores, and no fighting at all — and it is the
     * same table that binds the child next door
     * ([`life_stage`](Self::life_stage)).
     *
     * **Ignored, silently**, when the name normalizes to nothing, when the
     * player's figure is still **alive**, and when the figure is not living kin
     * of the late one (`sim_core::history::origins`'s `heirs`).
     * @param {string} name
     * @param {number} figure
     * @param {number | null} [coin_nonce]
     */
    queue_born_heir(name, figure, coin_nonce) {
        const ptr0 = passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        wasm.wasmsim_queue_born_heir(this.__wbg_ptr, ptr0, len0, figure, isLikeNone(coin_nonce) ? Number.MAX_SAFE_INTEGER : (coin_nonce) >>> 0);
    }
    /**
     * Queues a birth as the orphan of a town an army came through.
     *
     * Becomes `sim_core::Command::Born` with `Birth::Orphan`: the player is
     * somebody the world lost track of when that town fell, and the record the
     * life cites is the sack itself
     * (`docs/research/player-fantasies/fantasy-01-born-into-the-world.md` §2,
     * Phase 6 plan decision 3). The figure is created through the same
     * production door every figure in the world comes through, so it gets a
     * scheduled death, rolled facets and a record of its arrival — nothing
     * about it is special-cased, which is the parity test kept in code.
     *
     * `settlement` is the town's display number, exactly as
     * [`queue_claim`](Self::queue_claim)'s is, and the honest source of it is
     * the `settlement` column of an [`origins`](Self::origins) card whose
     * `kind` is the orphan's.
     *
     * **The name crosses raw and is normalized on the other side**, which is
     * [`queue_claim`](Self::queue_claim)'s rule and the same one wall: two
     * normalizations that disagree are a world that cannot be replayed.
     *
     * `coin_nonce` is which draw of the coined family name to keep, and may be
     * omitted — this door always coins, because an orphan has no line to
     * inherit, so it is the door the *coin another* button is most obviously
     * for (the module documentation's v7 note).
     *
     * **Ignored, silently and deterministically**, when the name normalizes to
     * nothing, when a player figure already exists by either door, and when the
     * town is not one this world has *in ruin today* — a tick has no error
     * channel ([`queue_dig`](Self::queue_dig)'s rule). Read
     * [`player_figure`](Self::player_figure) on the next tick to find out.
     * @param {string} name
     * @param {number} settlement
     * @param {number | null} [coin_nonce]
     */
    queue_born_orphan(name, settlement, coin_nonce) {
        const ptr0 = passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        wasm.wasmsim_queue_born_orphan(this.__wbg_ptr, ptr0, len0, settlement, isLikeNone(coin_nonce) ? Number.MAX_SAFE_INTEGER : (coin_nonce) >>> 0);
    }
    /**
     * Queues a birth as a child no chronicle recorded, of two people it did.
     *
     * Becomes `sim_core::Command::Born` with `Birth::Unrecorded`: the two
     * figures are named as the player's parents and the birth is written into
     * the log as one the chroniclers missed — honest for the reason the fantasy
     * states outright, that chronicles do not record every commoner birth
     * (§2). Both are real people the world already has, so the claims a noble
     * parent carries are the player's from the moment they are born
     * ([`claims`](Self::claims)).
     *
     * `mother` and `father` are figure display numbers — the `mother` and
     * `father` columns of an [`origins`](Self::origins) card whose `kind` is
     * the unrecorded child's. The town is not an argument because it is not a
     * second fact: both parents live in one settlement or they are not a pair
     * the door accepts, and the mother's own record says where.
     *
     * `coin_nonce` is which draw of a coined family name to keep, and may be
     * omitted. It has an effect **only when both parents are houseless**: a
     * child of a line inherits it, and no press can rename a house
     * (`sim_core`'s `history::house`).
     *
     * **Ignored, silently**, when the name normalizes to nothing, when a player
     * figure already exists, and when the pair is not two living grown
     * residents of one town, unrelated to each other and old enough to have had
     * this child. Every one of those is `sim_core`'s judgement and none of it
     * is re-stated here.
     * @param {string} name
     * @param {number} mother
     * @param {number} father
     * @param {number | null} [coin_nonce]
     */
    queue_born_unrecorded(name, mother, father, coin_nonce) {
        const ptr0 = passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        wasm.wasmsim_queue_born_unrecorded(this.__wbg_ptr, ptr0, len0, mother, father, isLikeNone(coin_nonce) ? Number.MAX_SAFE_INTEGER : (coin_nonce) >>> 0);
    }
    /**
     * Queues the player's claim on a name, and on a figure to hang it from.
     *
     * Becomes `sim_core::Command::Claim`: a historical figure enters the log
     * through the one door every other figure enters through, and from then on
     * the player's deeds cite it (`docs/plans/player-as-figure.md` §1, Phase 5
     * plan decision 1). The body is still the host's — this is the *record* of
     * a person, not an entity.
     *
     * **The name crosses raw and is normalized on the other side.**
     * `sim_core::history::PlayerName` is the one validation path — whitespace
     * collapsed, controls dropped, ends trimmed, cut to a bounded number of
     * bytes at a character boundary — and it is total, so there is nothing here
     * to refuse with. Doing any of that in this crate would be a second
     * spelling of a rule the replay format also has to agree with
     * (`sim_replay`'s `RecordedCommand::Claim` stores the normalized form), and
     * two normalizations that disagree are a world that cannot be replayed.
     * [`max_player_name_bytes`](crate::max_player_name_bytes) is the cap, for a
     * client that wants to say so before the player finishes typing.
     *
     * **Ignored, silently and deterministically**, when the name normalizes to
     * nothing, when a figure has already been claimed, or when `settlement`
     * names no town this world has — a tick has no error channel
     * ([`queue_dig`](Self::queue_dig)'s rule). A client that needs to know
     * whether the claim landed reads
     * [`player_figure`](Self::player_figure) on the next tick.
     *
     * **Unchanged by Phase 6, and still the other door** (plan decision 4 —
     * *two doors*): this is the in-world first-deed moment, where the three
     * `queue_born_*` methods above are the session-start one. A world has one
     * player, so whichever lands first makes the figure and the other is
     * refused — and a tick carrying both runs the birth first, whatever order
     * they were queued in (`sim_core`'s intent stage).
     * @param {string} name
     * @param {number} settlement
     */
    queue_claim(name, settlement) {
        const ptr0 = passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        wasm.wasmsim_queue_claim(this.__wbg_ptr, ptr0, len0, settlement);
    }
    /**
     * Queues putting one item on a tile out of nothing: **the developer's
     * door**.
     *
     * Becomes `sim_core::Command::Conjure`, and it is the one method on this
     * handle that is not a verb somebody in the world performs (chunk Fx-3 —
     * the owner's *"a debug menu that gives me items … items to test combat"*).
     *
     * **The gate is the caller's, and that is deliberate.** The browser offers
     * it under `?dev=1` alone (`web/src/dev/give.ts`); nothing here and nothing
     * in `sim_core` asks who is pressing, because a simulation that answered a
     * command differently depending on a flag outside the file would be a
     * simulation whose replays depend on something the file does not carry. The
     * command is recorded like every other, so a session that used the door
     * exports a replay that verifies — an honest replay of a development
     * session (`sim_core::conjure`).
     *
     * `kind` is `item_kind_ids`' id, `shape` is
     * [`item_shape_ids`](crate::item_shape_ids)' — `0` for the plain shape
     * twelve of the fourteen kinds come in — and `material` is an index into
     * [`material_names`](Self::material_names).
     *
     * Ignored, silently, when the chunk does not exist, when the kind or the
     * shape is one this build does not ship, when the shape is not one that
     * kind comes in, and when the material is not a row of this world's raws.
     * It asks nothing about a player figure: this is not a verb, so there is
     * nobody it has to be.
     * @param {number} x
     * @param {number} y
     * @param {number} z
     * @param {number} kind
     * @param {number} shape
     * @param {number} material
     */
    queue_conjure(x, y, z, kind, shape, material) {
        wasm.wasmsim_queue_conjure(this.__wbg_ptr, x, y, z, kind, shape, material);
    }
    /**
     * Queues taking a workshop's order at a bench, optionally naming the row.
     *
     * Becomes `sim_core::Command::Craft`. `recipe` is a row number and may be
     * omitted: **absent is the demand rule** — whatever that bench would have
     * taken anyway, most refined first — and a number is a row that has to be
     * runnable there today or the command refuses (`sim_core::RecipeChoice`,
     * and plan O5 as wave 1E-b narrowed it).
     *
     * `Option<u32>` rather than `Option<u16>` for the reason
     * [`queue_born_orphan`](Self::queue_born_orphan)'s nonce is an option at
     * all: wasm-bindgen maps a JavaScript `undefined` onto `None`, so a client
     * that offers no choice calls `queue_craft(x, y, z)` and gets the demand
     * answer. A number outside a `u16` is not a row this or any build has, and
     * it crosses as one the simulation then refuses — which is where a decision
     * about an unknown row belongs (`sim_core`'s `craft::choose`).
     *
     * Ignored for every reason [`queue_gather`](Self::queue_gather) is, and for
     * two of its own: a bench with nothing it can make, and a row this bench
     * does not run.
     * @param {number} x
     * @param {number} y
     * @param {number} z
     * @param {number | null} [recipe]
     */
    queue_craft(x, y, z, recipe) {
        wasm.wasmsim_queue_craft(this.__wbg_ptr, x, y, z, isLikeNone(recipe) ? Number.MAX_SAFE_INTEGER : (recipe) >>> 0);
    }
    /**
     * Queues digging one wall tile out.
     *
     * Becomes `sim_core::Command::Dig`: the wall becomes a floor of the same
     * material, that material lands on the new floor as spoil
     * ([`items_at`](Self::items_at)), and the connectivity labels merge — the
     * write set of `docs/research/df-systems-map/systems/map-and-blocks.md`
     * §11.5, cut to the systems Phase 1 has.
     *
     * **An illegal dig is dropped without a word.** A tile in a chunk that has
     * not been generated, a tile that is not a wall, or a tile some other dig
     * already opened in the same tick simply does not happen: there is no error
     * channel out of a tick, because a command was submitted a frame ago and a
     * throw here would describe a batch rather than an intention
     * (`sim_core`'s `dig` module). A client that needs to know whether its dig
     * landed reads [`TickSummary::dug_events`] — the digs that happened are
     * exactly the ones that say so.
     *
     * Several per tick are fine, and the order they are queued in does not
     * reach the world: the simulation applies them in tile order.
     * @param {number} x
     * @param {number} y
     * @param {number} z
     */
    queue_dig(x, y, z) {
        wasm.wasmsim_queue_dig(this.__wbg_ptr, x, y, z);
    }
    /**
     * Queues sending one of the player's companions on their way.
     *
     * Becomes `sim_core::Command::Dismiss`, and it is thinner than its mirror
     * on purpose: parting company asks no distance and no dials. Ignored when
     * there is no player figure, when the number is one this world has never
     * had, and when the figure follows somebody else.
     * @param {number} figure
     */
    queue_dismiss(figure) {
        wasm.wasmsim_queue_dismiss(this.__wbg_ptr, figure);
    }
    /**
     * Queues taking one worn piece off, back into the hands.
     *
     * Becomes `sim_core::Command::Doff`: [`queue_wear`](Self::queue_wear)'s
     * mirror, naming nothing for the same reason, and putting the piece in the
     * hands rather than on the ground — [`queue_drop`](Self::queue_drop) is the
     * press after this one.
     */
    queue_doff() {
        wasm.wasmsim_queue_doff(this.__wbg_ptr);
    }
    /**
     * Queues putting the first thing the player is carrying down on a tile.
     *
     * Becomes `sim_core::Command::Drop`: [`queue_take`](Self::queue_take)'s
     * mirror, with the same pick rule and the same refusals.
     * @param {number} x
     * @param {number} y
     * @param {number} z
     */
    queue_drop(x, y, z) {
        wasm.wasmsim_queue_drop(this.__wbg_ptr, x, y, z);
    }
    /**
     * Queues taking a piece of the marketplace's work at a tile.
     *
     * Becomes `sim_core::Command::Gather`. **There is no mine, fell or harvest
     * method here and there is not going to be one** (plan, decision 5): which
     * of the three a tile is a site for is the *simulation's* answer, read off
     * the tile by the same predicate that emits a town's own work, so a client
     * that offered three buttons would be keeping a copy of a judgement it does
     * not own.
     *
     * The cost of standing there is paid host-side, in walking, before the key
     * is pressed — the claim door scores no distance at all, because the
     * simulation has never known where the player is (Phase 3 plan, decision
     * 10).
     *
     * **Ignored, silently**, when no settlement is promoted, when nobody has
     * claimed a figure, when the tile is no kind of work, when somebody already
     * has it, and when the board is full.
     * @param {number} x
     * @param {number} y
     * @param {number} z
     */
    queue_gather(x, y, z) {
        wasm.wasmsim_queue_gather(this.__wbg_ptr, x, y, z);
    }
    /**
     * Queues interest in the world around a tile.
     *
     * Becomes `sim_core::Command::Interest`: every chunk column within
     * `radius_chunks` of the position's column — a Chebyshev square — is
     * generated inside the next tick if it does not exist yet. `z` is ignored
     * by worldgen (a column spans a fixed z-band) but is taken here so the
     * client can pass the player's tile without splitting it up.
     *
     * Idempotent, and expected every tick: an existing column is a lookup, and
     * generation happens *inside* the tick, never in response to a read
     * (Phase 1 plan, decision 1).
     *
     * `radius_chunks` is a `u8`, and the generated glue *truncates* a
     * JavaScript number outside 0..=255 rather than refusing it. Nothing is
     * added here to catch that, because the simulation already treats interest
     * as host input it does not trust: it clamps the radius to what a tick can
     * afford and the centre onto the addressable grid (`sim_core::worldgen`).
     * A second clamp here would be a second opinion about a limit that is not
     * this crate's to hold.
     * @param {number} x
     * @param {number} y
     * @param {number} z
     * @param {number} radius_chunks
     */
    queue_interest(x, y, z, radius_chunks) {
        wasm.wasmsim_queue_interest(this.__wbg_ptr, x, y, z, radius_chunks);
    }
    /**
     * Queues loosing an arrow at somebody.
     *
     * Becomes `sim_core::Command::Loose`, and it is
     * [`queue_strike`](Self::queue_strike)'s twin down to the citation: it
     * names a **person**, for the same argument, and it is the same exception
     * rather than a second one.
     *
     * Which bow and which arrow is the simulation's pick — the first of each in
     * the hands, in handle order — so the host never names a handle it read a
     * frame ago. One arrow is spent and lands on the target's tile.
     *
     * Aim is the host's (the phase's decision 2) and **no damage number or hit
     * verdict ever crosses back**: an arrow drawn flying between two points is
     * presentation of a fact the simulation has already resolved.
     *
     * Ignored, silently, for [`queue_strike`](Self::queue_strike)'s seven
     * reasons plus two of its own — no bow in the hands, no arrow in the hands
     * — and with [`bow_range_tiles`](crate::bow_range_tiles) in place of the
     * blade's reach.
     * @param {number} figure
     */
    queue_loose(figure) {
        wasm.wasmsim_queue_loose(this.__wbg_ptr, figure);
    }
    /**
     * Queues a command that does nothing.
     *
     * Exported because a replay's command log is allowed to contain one and a
     * host is allowed to send one as a keep-alive: a command that changes no
     * state changes no hash (`sim_core::Command::Nop`).
     */
    queue_nop() {
        wasm.wasmsim_queue_nop(this.__wbg_ptr);
    }
    /**
     * Queues leaving a written chronicle on the ground.
     *
     * Becomes `sim_core::Command::PlaceChronicle` (Phase 5 plan, decision 5):
     * a book is an ordered list of record ids and **not one word of prose**, so
     * nothing a player types enters the world through it. What it says when
     * somebody reads it is the simulation's own written register over those
     * records ([`chronicle_lines`](Self::chronicle_lines)).
     *
     * `entries` crosses as a `Float64Array` of record ids, which is the same
     * element type they arrive on ([`rumors`](Self::rumors),
     * [`beats`](Self::beats)) so a client hands back what it was given. **The
     * cap is the simulation's and is applied there**
     * (`sim_core::history::MAX_CHRONICLE_ENTRIES`, exported as
     * [`max_chronicle_entries`](crate::max_chronicle_entries)): a
     * seventeenth id is dropped by the type that carries them, ids the log
     * does not have are dropped when the command applies, and a book that
     * resolves to nothing is not left. Re-bounding here would be this crate
     * holding a second opinion about a number it does not own.
     *
     * Numbers that are not record ids are the one thing dropped on this side,
     * for [`queue_tell`](Self::queue_tell)'s reason: there is no [`EventId`]
     * to make out of a `NaN`.
     *
     * The position is where the book lies, z and all. Which town it belongs to
     * is the simulation's answer — the nearest one within reach — because the
     * simulation still does not learn where the *player* is standing: what it
     * learns is where a book is (plan cut list, "no presence-deeds").
     * @param {Float64Array} entries
     * @param {number} x
     * @param {number} y
     * @param {number} z
     */
    queue_place_chronicle(entries, x, y, z) {
        const ptr0 = passArrayF64ToWasm0(entries, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        wasm.wasmsim_queue_place_chronicle(this.__wbg_ptr, ptr0, len0, x, y, z);
    }
    /**
     * Queues telling the simulation which tile the player is standing on.
     *
     * Becomes `sim_core::Command::Stand`, and it is the **only** thing a host
     * ever says about the player's body (W11). The simulation holds it until
     * the next one supersedes it, and combat reads it for two things: how far
     * the player reaches, and how far anybody reaches the player.
     *
     * **Send it on crossing a tile, and only while somebody needs it** — plus
     * once immediately before every [`queue_strike`](Self::queue_strike),
     * [`queue_loose`](Self::queue_loose) and
     * [`queue_beckon`](Self::queue_beckon), because a verb aimed from a tile
     * the simulation never heard of is a defect. *Somebody* is two things since
     * wave 2F: a **blow** ([`WasmSim::engaged_with`], [`WasmSim::answering`]),
     * and a **follower** ([`WasmSim::leader_of`]) — a companion walks after the
     * tile the host states and has nothing else to walk after.
     *
     * That is a rule rather than an optimisation: it is what keeps a replay
     * carrying every fight, every accompanied walk and nothing else, and the
     * core's own documentation carries the argument.
     *
     * Never refused: a tile is a tile, and what it is worth is decided where it
     * is read.
     * @param {number} x
     * @param {number} y
     * @param {number} z
     */
    queue_stand(x, y, z) {
        wasm.wasmsim_queue_stand(this.__wbg_ptr, x, y, z);
    }
    /**
     * Queues swinging the player's hand at somebody.
     *
     * Becomes `sim_core::Command::Strike`, and it is the one command in the
     * vocabulary that cites a **person** rather than a place — `figure` is a
     * display number, the same handle every other read on this boundary uses.
     * The core's own paragraph is where that exception is argued: nothing ever
     * leaves the figure arena, so a display number belongs to one person for
     * the life of the world.
     *
     * Aim and timing are the host's (the phase's decision 2); everything from
     * the accuracy roll down is the simulation's, through the same pipeline a
     * watchman and a struck neighbour swing through. **No damage number and no
     * hit verdict ever crosses back** — what a client reads afterwards is a
     * condition word and a body that may have stopped moving.
     *
     * Ignored, silently, for seven reasons and **every one of them is a rule a
     * client can apply first**: no player figure, a number this world has never
     * had, a figure with no body in the promoted town, no
     * [`queue_stand`](Self::queue_stand) ever sent, the two tiles further apart
     * than [`strike_reach_tiles`](crate::strike_reach_tiles), a swing inside
     * [`strike_cooldown_ticks`](crate::strike_cooldown_ticks), and a hand that
     * cannot swing — dead, senseless, empty-handed, or a **child**
     * ([`WasmSim::life_stage`], and it is the same row that refuses the child
     * next door).
     * @param {number} figure
     */
    queue_strike(figure) {
        wasm.wasmsim_queue_strike(this.__wbg_ptr, figure);
    }
    /**
     * Queues picking the first thing lying on a tile up.
     *
     * Becomes `sim_core::Command::Take`, through the location union's one door
     * (`sim_core`'s `hands`). **The command names a tile and never an item**:
     * the pick rule — the first handle in handle order — is the simulation's,
     * spelled once, because a client naming a handle would be naming something
     * it read a frame ago.
     *
     * Ignored, silently, when nobody has claimed a figure, when the chunk does
     * not exist, when nothing on the tile is a thing hands lift, and when the
     * hands are already full ([`max_carried`](crate::max_carried)).
     * @param {number} x
     * @param {number} y
     * @param {number} z
     */
    queue_take(x, y, z) {
        wasm.wasmsim_queue_take(this.__wbg_ptr, x, y, z);
    }
    /**
     * Queues telling a settlement about a record.
     *
     * Becomes `sim_core::Command::Tell` (Phase 5 plan, decision 4): the
     * simulation runs the same first-telling-wins `learn` a caravan runs, and
     * *if and only if* the town genuinely learned something, a `WordBrought`
     * record cites the player's figure. Reputation is that record travelling
     * the roads with nothing changed in the propagation machinery — so the
     * deed is the record, and there is no reward to collect.
     *
     * `event` is an [`EventId`] as a JavaScript number, which is how every
     * record id crosses this boundary (`crate::convert`). A number that is
     * **not** an id — negative, fractional, past the exactly-representable
     * integers — queues nothing at all: `as` would turn it into *some* record
     * and tell a town about whatever that was, which is the one failure a
     * translation layer must not have.
     *
     * Ignored by the simulation, silently, in the ordinary case where the town
     * had already heard it, and in a world nobody has claimed a figure in.
     * @param {number} settlement
     * @param {number} event
     */
    queue_tell(settlement, event) {
        wasm.wasmsim_queue_tell(this.__wbg_ptr, settlement, event);
    }
    /**
     * Queues putting one carried piece of armour on, naming it or not.
     *
     * Becomes `sim_core::Command::Wear`, through the location union's one door
     * like every other item move. **It may name nothing at all**: with no
     * argument the piece is the simulation's pick — the most protective carried
     * thing whose body part is still bare — which is the tile rule taken to its
     * end, since a client that named a handle *it could not check* would be
     * naming something it read a frame ago.
     *
     * **And it may name one** (chunk S-2), because a panel that draws the hands
     * can point at a row and a hands list is not a pile: nothing but the player
     * changes it. `item` is the display number off a
     * [`carried`](WasmSim::carried) row, resolved sim-side against the player's
     * own hands and nowhere else — so a number that went stale between the read
     * and the press names nothing and the command does nothing.
     *
     * `Option<u32>` for [`queue_craft`](Self::queue_craft)'s reason:
     * wasm-bindgen maps a JavaScript `undefined` onto `None`, so a client that
     * offers no choice calls `queue_wear()` and gets the answer it always got.
     *
     * Ignored, silently, when nobody has claimed a figure, when nothing
     * wearable is in the hands, when every part it could cover is covered, when
     * the player's life stage does not fight — everything this build can wear
     * is armour — and, for a named press, when the number names nothing in the
     * hands or names something whose part is already covered.
     * @param {number | null} [item]
     */
    queue_wear(item) {
        wasm.wasmsim_queue_wear(this.__wbg_ptr, isLikeNone(item) ? Number.MAX_SAFE_INTEGER : (item) >>> 0);
    }
    /**
     * Queues putting one carried thing in front of everything else carried.
     *
     * Becomes `sim_core::Command::Wield` — **the equip choice, and it is an
     * order rather than a slot** (chunk S-2). The hands have always been a
     * list, and everything that reads *the first thing in the hands* reads that
     * list: the weapon a blow is resolved with, the bow, the arrow, the drop.
     * This says which handle is first, so a player who is carrying two blades
     * fights with the one they chose — and the simulation gained no new state
     * to say so with.
     *
     * `item` is the display number off a [`carried`](WasmSim::carried) row, and
     * it is the first command on this boundary to cite a **thing**. The
     * argument is the strike's, one noun over: a pile on a doorstep can move
     * under a client's last read and a pair of hands cannot, so a handle the
     * player no longer holds is refused in silence rather than resolved to
     * something else.
     *
     * It asks nothing about what the thing *is*: wielding a boulder is legal
     * and means the weapon pick walks past it. What a figure fights with is
     * still the simulation's answer and is unchanged.
     *
     * Ignored, silently, when nobody has claimed a figure and when the number
     * names nothing in the player's hands at the tick it lands on. Pressing it
     * twice on one thing is the second press doing nothing.
     * @param {number} item
     */
    queue_wield(item) {
        wasm.wasmsim_queue_wield(this.__wbg_ptr, item);
    }
    /**
     * Returns how many commands are waiting for the next tick.
     *
     * Diagnostics, and the honest way for a test to prove that [`tick`](Self::tick)
     * drains the queue.
     * @returns {number}
     */
    queued_commands() {
        const ret = wasm.wasmsim_queued_commands(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * Returns which way a ramp at the tile rises, or `-1`.
     *
     * `0` towards +x, `1` towards −x, `2` towards +y, `3` towards −y — the
     * mesher's own direction order, because this is the mesher's own rule: *a
     * ramp rises towards the first of those four in which the neighbouring tile
     * fills its cube sideways* (`crates/mesher/src/ramp.rs`, and
     * `crate::terrain` for the transcription).
     *
     * Exported so that a client's collision can stand on the slope the mesher
     * *drew* rather than on the flat tile top DF's attribute table implies. A
     * ramp is walkable and not solid ([`is_solid`](Self::is_solid)), so without
     * this a body walks through the picture and steps up a ledge it cannot see.
     * It answers a rendering question and therefore lives beside the mesher's
     * answer, not in the simulation: `sim_core` has no opinion about which way a
     * ramp faces, and `sim_core::worldgen` says so in as many words.
     *
     * `-1` means there is no direction to report — the tile is not a ramp, or
     * it is a ramp with no wall beside it, which the mesher draws as a flat
     * floor plate. [`tile_shape`](Self::tile_shape) separates those two.
     * @param {number} x
     * @param {number} y
     * @param {number} z
     * @returns {number}
     */
    ramp_rise_direction(x, y, z) {
        const ret = wasm.wasmsim_ramp_rise_direction(this.__wbg_ptr, x, y, z);
        return ret;
    }
    /**
     * Returns what the world remembers about one named thing, one line each.
     *
     * **The item-history projection** (`sim_core::relics`): who made it and how
     * good they were, when it was named and what for, every killing it is cited
     * on, and where the world last heard of it. Empty for anything that is not
     * a named thing, which is almost everything.
     *
     * The lines are the simulation's words with every handle already resolved
     * into a name, which is the same treatment a narrated record gets on its
     * way to the journal ([`describe`](Self::describe)): decision 11 says the
     * sentences are sim-side, and the resolver lives on the side that has the
     * arenas.
     *
     * **Where the world thinks it is, is not where it is.** The last line reads
     * the log, and the arena knows better (items §10.4, `sim_core::relics`'s
     * `Relic::last_known`): the two drift apart the moment somebody carries a
     * famous blade out of a gate, and that drift is the rumour mechanic rather
     * than a bug to close.
     * @param {number} item
     * @returns {string[]}
     */
    relic_lines(item) {
        const ret = wasm.wasmsim_relic_lines(this.__wbg_ptr, item);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]);
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Returns every named thing in the world as `item, kind, material, quality`
     * rows (`crate::flatten`).
     *
     * **The registry, in the arena's own order** (`sim_core::Snapshot::relics`),
     * which is the order the world hash folds in — so two hosts list the
     * world's relics identically and a self-check can assert on the order.
     * Short by construction: promotion is by named trigger.
     *
     * The same four-column layout an inventory row and a pile row cross in, so
     * one decoder reads all three; what each one is *called* is
     * [`item_name`](Self::item_name), per item, like every other string.
     * @returns {Float64Array}
     */
    relics() {
        const ret = wasm.wasmsim_relics(this.__wbg_ptr);
        var v1 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
        return v1;
    }
    /**
     * Restores a world from the text of a replay file.
     *
     * The world the file names is **rebuilt**, not loaded: the seed, the
     * material fingerprint and the bootstrap years come out of the envelope and
     * go straight into the ordinary constructor, and the run itself is re-played
     * by [`advance_replay`](Self::advance_replay). What this returns is the
     * world as construction left it — the state index 0 of the file's hash
     * sequence describes — with the log held and nothing of it run yet.
     *
     * `raws_toml` is the caller's, exactly as it is for the constructor: this
     * crate reads no files. It is checked against the fingerprint the file
     * carries before anything is built, because with a different material table
     * every hash differs from tick zero and *"the raws changed"* is a far more
     * useful sentence than *"diverged at tick 0"* (`sim-cli`'s `verify` refuses
     * the same file with the same sentence).
     *
     * # Errors
     *
     * Throws a JavaScript `Error` whose message names what is wrong with the
     * file: a version this build does not read, a document that is not one of
     * ours, a material table that is not this build's, or a world that does not
     * rebuild to the hash the file opens with. Every one of them is a refusal
     * to be shown to whoever chose the file, and none of them leaves a world
     * behind (`crate::record`'s `RestoreError`).
     * @param {string} replay_json
     * @param {string} raws_toml
     * @returns {WasmSim}
     */
    static restore_from_replay(replay_json, raws_toml) {
        const ptr0 = passStringToWasm0(replay_json, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(raws_toml, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmsim_restore_from_replay(ptr0, len0, ptr1, len1);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return WasmSim.__wrap(ret[0]);
    }
    /**
     * Returns the last tile the restored file names the player on, as
     * `x, y, z`, or an empty array.
     *
     * **A replay restores the world, not the session** (Phase 3 decision 10 —
     * the player is host-side). The journal, the ground somebody walked and the
     * people they met are the client's own and are in no file this crate
     * writes; what *is* in one is where the client said it was interested and
     * where it said the player stood, and this is the last of those. A host
     * that puts a player there is putting them within a chunk of where the
     * session it loaded left them.
     *
     * Empty for a world that was not restored, and for a file whose log never
     * named a tile — a recording of a session that never moved.
     * @returns {Int32Array}
     */
    restored_position() {
        const ret = wasm.wasmsim_restored_position(this.__wbg_ptr);
        var v1 = getArrayI32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Returns `[x, y]`: the column where the road from one settlement to
     * another leaves the first one's streets.
     *
     * **Where a road meets a town** (`sim_core::Snapshot::road_mouth`, wave
     * 1.5). The roads between settlements are terrain now, and they are bedded
     * from the mouth of the main street on the side that faces the other town —
     * the same street the rampart's gate is cut in. A client puts a signpost
     * there; guessing an offset off the settlement's centre, which is what it
     * used to do, now stands the post inside somebody's house.
     *
     * Empty for a handle this world does not have, on
     * [`settlement_name`](Self::settlement_name)'s terms: asking about
     * somewhere that is not there is a normal thing for a client walking a list
     * of routes to do.
     * @param {number} from
     * @param {number} to
     * @returns {Int32Array}
     */
    road_mouth(from, to) {
        const ret = wasm.wasmsim_road_mouth(this.__wbg_ptr, from, to);
        var v1 = getArrayI32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Returns a settlement's living named figures as
     * `figure, anxiety, bravery, trust, gregariousness` rows
     * (`crate::flatten`).
     *
     * **The roster** (Phase 3 plan, decision 8): the people the visited
     * settlement can put in the street. Handles rather than bodies — the
     * simulation never learns where an NPC is standing, and a
     * [`FigureId`]-stable roster is what lets a client anchor one to the same
     * person across a session.
     *
     * Living only. The dead keep their records and their graves, and neither is
     * somebody to talk to ([`features`](Self::features)).
     * @param {number} settlement
     * @returns {Int32Array}
     */
    roster(settlement) {
        const ret = wasm.wasmsim_roster(this.__wbg_ptr, settlement);
        var v1 = getArrayI32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Returns the roads out of a settlement as `to, cost` pairs
     * (`crate::flatten`), cheapest first.
     *
     * The paths news and refugees travel — and the only thing this simulation
     * means by a road (`sim_core::history::roads`: a destination and a price,
     * no geometry).
     * @param {number} settlement
     * @returns {Int32Array}
     */
    routes(settlement) {
        const ret = wasm.wasmsim_routes(this.__wbg_ptr, settlement);
        var v1 = getArrayI32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Returns the towns still in ruin as
     * `settlement, sack, since_year, since_day` rows (`crate::flatten`).
     *
     * **Interim honesty** (Phase 5 plan, decision 11): a projection over the
     * log — a recent sack with no recovery since — so a client can draw ruin
     * props, thin the crowd and quieten the market without the simulation
     * pretending it holds structural damage in world data. That is Phase 7's,
     * per the owner's recorded art direction, and this flag is what stands in
     * until then.
     *
     * **A town that is not in ruin has no row.** The list is short in a world
     * at peace and empty in one that has never been at war, which is the
     * answer rather than an omission; a client reads it into a set once a day
     * and asks that set about the town it is standing in.
     *
     * The sack rides along because every surface in this project is held to
     * traceability: the ruin a client draws cites the battle that caused it,
     * which is the same [`EventId`] the town's own rumor store is carrying and
     * the same one a grave outside the walls is cut with.
     *
     * Derived on the call and never stored, like every other viewer here — so
     * a build that draws ruin for a year longer draws *ruin* for a year longer,
     * and no replay can tell.
     * @returns {Float64Array}
     */
    ruins() {
        const ret = wasm.wasmsim_ruins(this.__wbg_ptr);
        var v1 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
        return v1;
    }
    /**
     * Returns what a settlement has heard as
     * `event, learned_year, learned_day, hops, via` rows (`crate::flatten`),
     * oldest first.
     *
     * **A rumor is a reference, never a copy** (Phase 3 plan, decision 2): the
     * [`EventId`] always points at the true record, and what a player hears is
     * vague or sure because of the *delivery* ([`speak`](Self::speak)), not
     * because anything degraded on the way. That is what makes the phase's
     * exit criterion checkable — a price, a grave and an overheard line
     * resolving to the same id is a comparison a debug view performs.
     * @param {number} settlement
     * @returns {Float64Array}
     */
    rumors(settlement) {
        const ret = wasm.wasmsim_rumors(this.__wbg_ptr, settlement);
        var v1 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
        return v1;
    }
    /**
     * Returns the world's history as a book keeps it, as
     * `ordinal, anchor, began_year, began_day, ended_year, ended_day, lines`
     * rows (`crate::flatten`), oldest first.
     *
     * **The Book of Eras** (Phase 6 plan, decision 2 and O5): the whole log cut
     * into ages, each one named after the record it turns on — a war that
     * toppled a ruling line, or the fall of a settlement with a history behind
     * it. Derived on the call and never stored, so a build that segments the
     * world differently segments a *book* differently and no replay can tell.
     *
     * `anchor` is the record the age turns on and resolves in the log like every
     * other id on this surface; `lines` is how many sentences the chapter has,
     * which is the length [`era_lines`](Self::era_lines) and
     * [`era_citations`](Self::era_citations) both come back.
     *
     * An age still running carries `-1` in both of its `ended` columns, which is
     * how this boundary spells *there is nothing to report here* everywhere else
     * (`crate::flatten`). There is at most one such age and it is the last.
     *
     * Empty for a world with no history yet, which is every world at tick zero
     * and every session that asked for no bootstrap.
     * @returns {Float64Array}
     */
    saga_eras() {
        const ret = wasm.wasmsim_saga_eras(this.__wbg_ptr);
        var v1 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
        return v1;
    }
    /**
     * Returns the seed the world was created with.
     *
     * A `BigInt` on the JavaScript side, for the same reason the constructor
     * takes one.
     * @returns {bigint}
     */
    seed() {
        const ret = wasm.wasmsim_seed(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * Returns how many ticks this *session* has run for.
     *
     * Zero at construction whatever the bootstrap was, which is the number a
     * replay's hash sequence is indexed by: a replay describes the run, and
     * the years before it are an input to construction rather than ticks
     * anybody performed (`sim_replay`, and `crate::record`).
     * @returns {number}
     */
    session_ticks() {
        const ret = wasm.wasmsim_session_ticks(this.__wbg_ptr);
        return ret;
    }
    /**
     * Sets how fast this session's reading says spoken memory fades, in days.
     *
     * **The decay knob** (Phase 6 plan, decision 7 and O7): a
     * `sim_core::Snapshot::with_salience_decay` lens, wired to the one door
     * every witnessing read already comes through. A telling loses a step of
     * detail per half-life on top of the mouths and the years it already loses
     * it for, and the square stops offering word beats about stories it heard
     * more than a few half-lives ago — so **spoken memory fades and the
     * chronicles and the stones become the only permanent memory**, which is
     * the feel the rider exists to let the owner judge (CK3 lane 1 §4).
     *
     * # It is unhashed, and that is the whole of its safety
     *
     * `Snapshot::heard_by`'s discipline, quoted because it is the same one:
     * *"a property of the reading, not of the world … nothing here is hashed —
     * a snapshot is a borrow of the world plus the question being asked of
     * it."* So this changes sentences and cannot change a run: it is not a
     * command, it is not queued, it is not in the replay, and two hosts
     * replaying one command log with different knobs agree on every hash and
     * disagree about how well anybody remembers. That is what makes the knob
     * replay-safe by construction rather than by care — and why the client may
     * put it on a URL parameter the owner flips in a production build without
     * hiding it behind a dev flag.
     *
     * # Which reads change
     *
     * Everything with a **mouth** behind it, because those are the projections
     * that go through `sim_core`'s own fading: [`speak`](Self::speak),
     * [`speak_of`](Self::speak_of), [`speak_pulse`](Self::speak_pulse),
     * [`speak_flight`](Self::speak_flight), and the word beats in
     * [`beats`](Self::beats). Nothing else moves — a grave, a market board, a
     * book on a shelf and the Book of Eras itself are **written**, and a world
     * where the stones forgot would be a world with no permanent memory at all
     * (`crate::witness`'s module docs list the split).
     *
     * # The argument
     *
     * Days, as `sim_core::history::SALIENCE_HALF_LIFE_DAYS` counts them — two
     * years is the default that constant offers, and this crate does not apply
     * it, because a default that had to be turned off would be a rider that
     * shipped on.
     *
     * **Hostile input is expected**, since the number comes off a URL:
     * a negative, a `NaN` or an infinity turns the lens **off**, and zero is
     * passed straight through to the simulation's own documented answer for it
     * — *everything is forgotten* — rather than being second-guessed here
     * (`crate::convert`'s `half_life_from_f64` states every rule). There is no
     * getter, because the caller is the only thing that has ever set it.
     * @param {number} half_life_days
     */
    set_salience_decay(half_life_days) {
        wasm.wasmsim_set_salience_decay(this.__wbg_ptr, half_life_days);
    }
    /**
     * Returns how many settlements the world has.
     * @returns {number}
     */
    settlement_count() {
        const ret = wasm.wasmsim_settlement_count(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * Returns what a settlement is called, or the empty string for a handle
     * this world does not have.
     *
     * A string per call rather than a `string[]` beside
     * [`settlements`](Self::settlements), which is decision 11's rule and not a
     * micro-optimisation: names are pulled when a client draws a label or opens
     * a panel, and a bulk array of them would be marshalled — allocation, hidden
     * class and copy per entry — on a query a client makes to find out where the
     * towns are.
     *
     * The empty string rather than an exception, for the reason
     * [`tile_shape`](Self::tile_shape) answers `0` off the map: asking about
     * somewhere that is not there is a normal thing for a client walking a list
     * to do, and it is not an error.
     * @param {number} settlement
     * @returns {string}
     */
    settlement_name(settlement) {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.wasmsim_settlement_name(this.__wbg_ptr, settlement);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Returns every settlement as `id, x, y` triples (`crate::flatten`).
     *
     * The whole map, because it is sixteen rows in the reference world and a
     * client needs all of them to draw one road: a route names its far end by
     * handle and roads have no geometry, so a straight line between two
     * positions is what a client draws (Phase 3 plan, decision 8).
     *
     * The positions are world tile coordinates on the same grid everything else
     * in this crate uses. They are *not* chunk coordinates and the columns under
     * them need not exist: a settlement sits on a world-field column, which is a
     * pure function of the seed (`sim_core::history::sites`).
     *
     * Copies. Pull it when [`TickSummary::records_added`](crate::TickSummary)
     * says a record landed, not per frame — a settlement is founded about once
     * a decade.
     * @returns {Int32Array}
     */
    settlements() {
        const ret = wasm.wasmsim_settlements(this.__wbg_ptr);
        var v1 = getArrayI32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Returns the books on the shelf standing at a tile as `item, citations`
     * rows (`crate::flatten`).
     *
     * **The library beat's data** (plan, decision 14). Empty for a tile with no
     * shelf on it *and* for a shelf nobody has written a book onto, which are
     * two different things — [`has_shelf`](Self::has_shelf) is what tells them
     * apart, and the difference is the whole of the empty-shelf invitation: a
     * town with no library says nothing, and a town whose library is bare says
     * that nobody here has written anything down.
     * @param {number} x
     * @param {number} y
     * @param {number} z
     * @returns {Float64Array}
     */
    shelved(x, y, z) {
        const ret = wasm.wasmsim_shelved(this.__wbg_ptr, x, y, z);
        var v1 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
        return v1;
    }
    /**
     * Returns what one figure says when a player asks them for news, or
     * `undefined` when they have none.
     *
     * The line crosses **whole** (Phase 3 plan, decision 11): `sim_core`
     * composes it out of the record, the speaker's four dials, how long ago it
     * happened and how many mouths it came through
     * ([`speech::line`], decision 9), and the client displays the string. The
     * pieces it was built from — [`Telling`]'s opener, the age phrasing, the
     * facet clause — are display detail and not a boundary contract; a client
     * that assembled its own sentence out of them would be a client authoring
     * the world's fiction.
     *
     * # Which of the things the town knows they reach for
     *
     * Somebody has to choose, and knowledge is sim-side (decision 8), so the
     * choice is here. It is `sim-cli`'s legends preview rule
     * (`preview_rumors`), one speaker at a time: **the speaker's place in the
     * roster decides how far out on the road they reach**, and they take the
     * freshest thing the settlement has heard at that remove. So the first
     * person in the street tells you what happened here, the second what
     * somebody carried in, the third what came down the road through other
     * mouths — and a crowd delivers the same war at three different
     * [`Telling`]s, which is the whole visible effect decision 9 claims. A town
     * that has heard nothing at a given remove falls back to counting down the
     * store from the freshest, so eight neighbours still say eight different
     * things.
     *
     * It is a pure function of the world: no counter, no rotation, no state.
     * Ask the same person twice and they say the same thing, until the town
     * hears something newer.
     * @param {number} figure
     * @returns {Spoken | undefined}
     */
    speak(figure) {
        const ret = wasm.wasmsim_speak(this.__wbg_ptr, figure);
        return ret === 0 ? undefined : Spoken.__wrap(ret);
    }
    /**
     * Returns what a group of refugees says about what they are running from,
     * or `undefined`.
     *
     * The same projection as [`speak`](Self::speak) over the same kind of
     * record, with two differences that are both facts about the speaker: the
     * telling is [`Telling::FirstHand`] because they were there, and the
     * delivery is uncoloured because a group has no personality to colour it
     * with (`NOBODY_IN_PARTICULAR`, history §8.4 — populations, not people).
     *
     * A group is named by the battle it fled, which is also its row's first
     * column in [`flights`](Self::flights).
     * @param {number} event
     * @returns {Spoken | undefined}
     */
    speak_flight(event) {
        const ret = wasm.wasmsim_speak_flight(this.__wbg_ptr, event);
        return ret === 0 ? undefined : Spoken.__wrap(ret);
    }
    /**
     * Returns what one figure says about one *named* record, or `undefined`.
     *
     * [`speak`](Self::speak)'s other door and the one a staged beat wants: the
     * caller has the record — the battle a crier is crying, the beat the
     * director is delivering — rather than whatever the speaker would have
     * reached for. Phase 4 plan, decision 5: the town's reaction to a battle
     * landing is the nearest figures saying **the record's line**, and the
     * alternative would be a client composing a sentence about a record, which
     * is the one thing above this seam that is never allowed (devlog #2).
     *
     * **The provenance stays the world's.** The hop count and the day it was
     * learned come off the speaker's own settlement's store
     * ([`spoken_at`](sim_core::Snapshot::spoken_at)), so a crier can only cry
     * what the town has actually heard and the delivery gets vaguer with the
     * town's own distance from it (Phase 3 plan, decision 9).
     *
     * `undefined` — rather than a substitute — when that settlement has never
     * heard of the record: a speaker who said something *else* would put the
     * wrong [`EventId`] in the player's journal, which is worse than silence.
     * @param {number} figure
     * @param {number} event
     * @returns {Spoken | undefined}
     */
    speak_of(figure, event) {
        const ret = wasm.wasmsim_speak_of(this.__wbg_ptr, figure, event);
        return ret === 0 ? undefined : Spoken.__wrap(ret);
    }
    /**
     * Returns what the traveller walking with a caravan says, or `undefined`.
     *
     * **The sender's own store, at the sender's own hops** (Phase 4 plan,
     * decision 2). A caravan's news is not the news of the town it is walking
     * into: the traveller is a roster figure of the *origin*
     * ([`caravan_talker`](sim_core::Snapshot::caravan_talker)), what they carry
     * is the freshest thing that origin has heard — the same record
     * `sim_core::history`'s `rumor::carry` drew the delivered batch from, and
     * the same one the caravan's beat carries — and the delivery is
     * composed against that settlement's knowledge
     * ([`spoken_at`](sim_core::Snapshot::spoken_at)).
     *
     * Composed rather than built: every part of it is a projection `sim_core`
     * already exports, and this method is the three of them in a row.
     *
     * # A traveller is not standing in the town they are talking about
     *
     * Which is why the line comes through
     * [`spoken_on_the_road`](sim_core::Snapshot::spoken_on_the_road) rather
     * than [`spoken_at`](sim_core::Snapshot::spoken_at): the column is *from*
     * the origin and is standing nowhere the simulation knows (decision 8), so
     * the origin's own news is news from **back home** and never news from
     * here. A crier standing in their own square is the other door, and the two
     * say the same record in different words on purpose.
     *
     * `undefined` for a caravan this turn did not send, for a shipper with
     * nobody left to send, and for one whose store is empty — which is not a
     * failure but the answer, the same way a townsman with no news says nothing
     * ([`speak`](Self::speak)).
     * @param {number} pulse
     * @returns {Spoken | undefined}
     */
    speak_pulse(pulse) {
        const ret = wasm.wasmsim_speak_pulse(this.__wbg_ptr, pulse);
        return ret === 0 ? undefined : Spoken.__wrap(ret);
    }
    /**
     * Returns `true` when the tile is a thing **standing on** the ground rather
     * than the ground itself.
     *
     * `sim_core::Snapshot::stands_on_ground`, and it is a **footing** question
     * rather than a walking one (wave 1.5's graybox ruling). A trunk's base
     * replaces its column's surface tile now, and a town's wall has always
     * stood on one — so a host scanning a column downwards for *"how high is
     * the ground here"* stops at the first solid tile and puts whatever it was
     * placing on top of the tree, or on the roof
     * (`web/src/witness/crowd.ts`'s `surfaceZ`, which is where both were
     * found).
     *
     * Asked rather than inferred, on [`work_at`](Self::work_at)'s terms: what
     * separates a trunk, a crown and a house wall from the rock under them is a
     * variant, a material and a town plan, and none of those is vocabulary a
     * client keeps a copy of.
     *
     * Asked only about a tile that already claimed to be solid, which keeps it
     * one extra call per *probe* rather than one per level.
     * @param {number} x
     * @param {number} y
     * @param {number} z
     * @returns {boolean}
     */
    stands_on_ground(x, y, z) {
        const ret = wasm.wasmsim_stands_on_ground(this.__wbg_ptr, x, y, z);
        return ret !== 0;
    }
    /**
     * Returns the tile the simulation is holding as the player's, as
     * `[x, y, z]`, or an empty array if it holds none.
     *
     * The read-back half of [`WasmSim::queue_stand`], and it exists so a host
     * can tell *"the simulation has not heard from me"* from *"the simulation
     * has last tick's tile"* — one refusal apart at
     * [`WasmSim::queue_strike`], and a client that could not tell them apart
     * would have to guess which of its own prompts was true.
     *
     * Empty in every session nobody has ever been engaged in, which is nearly
     * all of them. **It is not a position**: nothing here says the player has a
     * body in the simulation, which they still do not (`sim_core`'s `local`).
     * @returns {Int32Array}
     */
    stood() {
        const ret = wasm.wasmsim_stood(this.__wbg_ptr);
        var v1 = getArrayI32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Advances the simulation exactly one tick, applying and clearing the
     * queued commands.
     *
     * Called from the host's fixed-step accumulator and from nowhere else. It
     * takes no time argument on purpose: the simulation knows ticks, not
     * seconds (guide §8.1).
     *
     * The returned summary is a wasm-bindgen object; `free()` it, or let the
     * finalizer do it once the client has read what it needs out.
     *
     * The tick's bulk output — the dirty chunks, the events — is flattened
     * *here*, once, into the summary's owned buffers (`crate::flatten`), rather
     * than being re-derived when the client asks for it. A `TickOutput` borrows
     * nothing and is dropped at the end of this call, so the alternative would
     * be keeping the core's types alive behind the boundary; and a client that
     * asks for the same array twice should pay for two copies, not for two
     * translations.
     * @returns {TickSummary}
     */
    tick() {
        const ret = wasm.wasmsim_tick(this.__wbg_ptr);
        return TickSummary.__wrap(ret);
    }
    /**
     * Returns how many ticks the world has run for.
     *
     * **The world's clock, not the session's.** A world constructed on a
     * bootstrap starts on the last tick of those years rather than on zero
     * (`WasmSim::new`), so this is what the HUD should show and
     * [`WasmSim::session_ticks`] is what a replay counts.
     *
     * A JavaScript number, exact for every tick a session can reach
     * (`crate::convert`).
     * @returns {number}
     */
    tick_count() {
        const ret = wasm.wasmsim_tick_count(this.__wbg_ptr);
        return ret;
    }
    /**
     * Returns what is lying on a tile as `item, kind, material, quality` rows
     * (`crate::flatten`).
     *
     * [`carried`](Self::carried)'s other end and the same layout, so a client
     * draws a pile and an inventory row through one decoder.
     *
     * **The cheap way to use it is not to scan.** A tick says which tiles
     * changed ([`TickSummary::dug_events`](crate::TickSummary::dug_events)) and
     * the board says which tiles are being worked ([`jobs_at`](Self::jobs_at));
     * asking about those is a handful of calls, and asking about every tile in
     * an interest square is the expensive way to answer a question nobody has.
     *
     * It supersedes [`items_at`](crate::WasmSim::items_at), which stays for the
     * reason a superseded export usually goes: that one answers what a pile is
     * *made of* in one `Uint16Array`, which is what a particle or a colour
     * wants, and this one answers what is in it.
     * @param {number} x
     * @param {number} y
     * @param {number} z
     * @returns {Float64Array}
     */
    tile_items(x, y, z) {
        const ret = wasm.wasmsim_tile_items(this.__wbg_ptr, x, y, z);
        var v1 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
        return v1;
    }
    /**
     * Returns the tile's shape id, and `0` for a tile that does not exist.
     *
     * DF's `tiletype_shape` value as `sim_core::map::TileShape` carries it,
     * gaps included: `None` 0, `Empty` 1, `Floor` 2, `Wall` 5, `Ramp` 10,
     * `RampTop` 11 (`crate::terrain`). The unused numbers are DF shapes this
     * build has not shipped rather than free ids, so a client may match on the
     * ones it knows and treat the rest as terrain it does not understand.
     *
     * The one shape a client cannot get away with ignoring is `Ramp`, because a
     * ramp is not a box: see [`ramp_rise_direction`](Self::ramp_rise_direction)
     * and [`ramp_shape_id`](crate::ramp_shape_id).
     * @param {number} x
     * @param {number} y
     * @param {number} z
     * @returns {number}
     */
    tile_shape(x, y, z) {
        const ret = wasm.wasmsim_tile_shape(this.__wbg_ptr, x, y, z);
        return ret;
    }
    /**
     * Returns how far through the day it is, as a fraction in `[0, 1)`.
     *
     * The world's clock at a finer grain than the date: the tick within the
     * day, divided by `sim_core::calendar::TICKS_PER_DAY`. **A derived seam,
     * not state** (Phase 5 plan, decision 8) — the simulation stores no
     * sub-day time and nothing in it reads one, so this is arithmetic over
     * [`tick_count`](Self::tick_count) and a constant that lives in
     * `sim_core`. Both halves are the core's on purpose: a client that
     * measured its own day would be keeping a second calendar, and one that
     * wrote 36,000 down would be keeping a stale one (the phase's
     * cross-cutting rule: *the calendar is spelled once*).
     *
     * **Zero is the day boundary**, which is where the date turns
     * ([`TickSummary::day_changed`]) and therefore where the client reads
     * first light. That reading is the client's — the simulation has no sun
     * and no opinion about when one comes up (`web/src/render/daylight.ts`
     * keys the sky to this number); what crosses here is only *how far through
     * the day the world is*.
     *
     * Cheap enough to ask per frame — one remainder and one division — which
     * is why it is a method on the handle rather than a field on the tick
     * summary: a sky is drawn on rendered frames, and those do not line up
     * with the fixed step that produces summaries.
     * @returns {number}
     */
    time_of_day() {
        const ret = wasm.wasmsim_time_of_day(this.__wbg_ptr);
        return ret;
    }
    /**
     * Returns how many consecutive ticks the **held** interest has not covered
     * the promoted settlement.
     *
     * Zero while it is covered, and zero while nothing is promoted — so the
     * number is only meaningful beside
     * [`promoted_settlement`](Self::promoted_settlement). At
     * [`demotion_ticks`](Self::demotion_ticks) the town demotes and every body
     * standing in it is dropped, which is the countdown the debug window shows.
     *
     * **What it counts changed at item 6 and the number did not** (ruled
     * 2026-08-28). It used to count ticks since an interest command was last
     * *heard*, so a host that sent one only when it changed — which is every
     * host, because a repeat is not news — ran the countdown down while the
     * player stood still, and the tier went out from under them after
     * twenty-five seconds. The simulation holds the interest until it is told
     * otherwise and evaluates coverage every tick against what it holds, so
     * this counts ticks the *held* square did not reach the town: silence never
     * advances it, and a new interest that has walked away does.
     * @returns {number}
     */
    uncovered_ticks() {
        const ret = wasm.wasmsim_uncovered_ticks(this.__wbg_ptr);
        return ret;
    }
    /**
     * Returns what the world says to somebody waking into one of the lives it
     * offers, or the empty string.
     *
     * **The opening's payoff beat** (`docs/plans/ui-graduation-plan.md`, chunk
     * S-1): the veil lifts on the street at dawn and the world says its first
     * words to the player, in one written sentence over the life they chose —
     * the town by its own name, the year, the birth year where a record holds
     * one, the parents and the line where the card has them, and an honest
     * shape where it has none ([`speech::waking`]).
     *
     * **The sentence is the simulation's, and that is the whole point of this
     * export.** The client's charter forbids assembling prose out of fields, so
     * the beat had no composer until this one: nothing above this seam picks a
     * clause, and a client that laid one out itself would be writing the
     * world's dialogue.
     *
     * `origin` is the **row index** into [`origins`](Self::origins), counting
     * from zero, exactly as [`origin_claims`](Self::origin_claims) takes one —
     * a question about *the list this call produced*, asked in the same breath.
     * One past the end is the empty string, which is the wall's convention for
     * a handle it cannot place.
     *
     * `name` is what the player typed, through the same wall a birth goes
     * through (`sim_core::history::PlayerName`), so the sentence carries the
     * name the record will carry and not a second normalization of it. **The
     * empty string is the nameless form and it is the default**, not a
     * degradation: an unnamed session never triggers a name-dependent beat
     * (owner ruling), and the sentence simply does not address anybody by name.
     *
     * Nothing about this world moves: it is a read like every other export
     * here, derived per call off the same projection the cards come from.
     * @param {number} origin
     * @param {string} name
     * @returns {string}
     */
    waking_line(origin, name) {
        let deferred2_0;
        let deferred2_1;
        try {
            const ptr0 = passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len0 = WASM_VECTOR_LEN;
            const ret = wasm.wasmsim_waking_line(this.__wbg_ptr, origin, ptr0, len0);
            deferred2_0 = ret[0];
            deferred2_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred2_0, deferred2_1, 1);
        }
    }
    /**
     * Returns what the town holds against a figure and has not answered, as
     * `sim_core::history::CrimeKind`'s own id, or -1 for nothing.
     *
     * **The blotter as a read** (`sim_core`'s `watch`, decision 11): the
     * gravest open row, which is the same row the watch's own dispatch pass
     * would answer — so a client that puts an accusation in a guard's mouth is
     * reading the simulation's decision rather than making one.
     *
     * It answers for the **player's** figure too, and that is the case it
     * exists for: the player has no body, so a guard walks to the scene of the
     * theft rather than to them, and this is what lets the client say why he is
     * standing there.
     *
     * An id rather than a word, on the seat table's terms (`crate::flatten`'s
     * `claims`): the ids are append-only, so a crime this build's client does
     * not know is one a later one minted.
     * @param {number} figure
     * @returns {number}
     */
    wanted_for(figure) {
        const ret = wasm.wasmsim_wanted_for(this.__wbg_ptr, figure);
        return ret;
    }
    /**
     * Returns the display number of the piece a wear would put on next, or
     * zero.
     *
     * **The read that lets a client refuse out loud**
     * (`sim_core::Snapshot::wearable`): the simulation ignores a wear it cannot
     * honour, so a panel offering the verb says *"you have nothing to put on"*
     * first — and it can only be honest about that by asking the pick itself,
     * because what decides is whether a body **part** is bare rather than how
     * much armour is in the hands.
     *
     * A handle rather than a flag, on [`bow`](Self::bow)'s terms and for its
     * reason: a prompt can then name the thing through
     * [`item_label`](Self::item_label).
     * @returns {number}
     */
    wearable() {
        const ret = wasm.wasmsim_wearable(this.__wbg_ptr);
        return ret;
    }
    /**
     * Returns the display number of the thing the player has chosen to hold in
     * front, or zero for hands nobody has arranged.
     *
     * **The read side of [`queue_wield`](crate::WasmSim::queue_wield)** (chunk
     * S-2, `docs/decisions/player.md` §24), and the difference it answers is
     * the whole of why it is its own read rather than the head of
     * [`carried`](Self::carried): `carried_by(player).first()` is *whatever
     * happens to be first* and this is *what they chose*, and only the second
     * one may light a slot. A surface that lit the head of the carry list would
     * light a boulder somebody picked up a minute ago.
     *
     * A handle rather than a flag, on [`bow`](Self::bow)'s terms and for its
     * reason: what the thing is *called* crosses per call
     * ([`item_label`](Self::item_label)), because the simulation owns the words.
     * Zero is the honest nothing for the same reason it is there — no item ever
     * has display number zero, because arena slots are allocated from one.
     *
     * **It is not *the weapon you would swing with***, and the difference is
     * filed rather than smuggled (§24.8): `combat::weapon_of` is private, so a
     * client can honestly draw the thing in the hand and nothing more. Wielding
     * a boulder is legal and means the weapon pick walks past it.
     *
     * Zero in every world nobody has claimed a figure in, and for the
     * overwhelming majority of players, who have never pressed the door at all.
     * @returns {number}
     */
    wielded() {
        const ret = wasm.wasmsim_wielded(this.__wbg_ptr);
        return ret;
    }
    /**
     * Returns which kind of gathering a tile is a site for, or `-1` for none.
     *
     * **The refusal's read** (`sim_core::Snapshot::work_at`, wave 1.5's graybox
     * ruling: *every refused action displays a judgeable message*). The
     * marketplace's own legality predicate, asked *before* the verb is pressed
     * so that a client can say `"nothing to gather here"` instead of queueing a
     * command the world refuses in silence — which is the same bargain
     * `WasmWitnessSource::lots`' `trade` column makes for the workshop
     * verb, and it is what keeps the *judgement* sim-side while the *sentence*
     * stays the client's.
     *
     * **Asking is not deciding.** Nothing here is a promise: the command
     * re-validates against the world as it is on the tick it lands on, and a
     * tree somebody felled in between is refused exactly as it always was.
     *
     * The ids are `sim_core::JobKind`'s own and are append-only, checked at
     * startup against [`job_kind_ids`](crate::job_kind_ids) like every other
     * vocabulary the client keeps a copy of. `-1` rather than `undefined`
     * because this is a hot-ish read a client makes on a keystroke and a
     * nullable across the boundary allocates.
     * @param {number} x
     * @param {number} y
     * @param {number} z
     * @returns {number}
     */
    work_at(x, y, z) {
        const ret = wasm.wasmsim_work_at(this.__wbg_ptr, x, y, z);
        return ret;
    }
    /**
     * Returns the world hash as of the last completed tick, as `0x` and
     * sixteen hex digits.
     *
     * The notation the replay format uses, so a hash on the HUD and a hash in a
     * replay file are the same string (`crate::convert`).
     * @returns {string}
     */
    world_hash_hex() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.wasmsim_world_hash_hex(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Returns what the player is wearing as `item, kind, material, quality`
     * rows (`crate::flatten`).
     *
     * [`carried`](Self::carried)'s twin one union arm over — the read
     * [`queue_wear`](crate::WasmSim::queue_wear) made possible — and the same
     * layout, so a panel draws the hands and the body through one decoder.
     *
     * **What each piece protects is deliberately absent.** A client that drew
     * *"on the head"* beside a helm would be keeping a copy of the armour table
     * and of the body plan; what it draws instead is the label
     * ([`item_label`](Self::item_label)), which is the simulation's own words
     * for the thing (decision 11's split).
     *
     * Empty for almost every session forever: nothing in the economy hands a
     * townsman a breastplate yet, and a player has to make one.
     * @returns {Float64Array}
     */
    worn() {
        const ret = wasm.wasmsim_worn(this.__wbg_ptr);
        var v1 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
        return v1;
    }
}
if (Symbol.dispose) WasmSim.prototype[Symbol.dispose] = WasmSim.prototype.free;

/**
 * Returns every surface id a spotlight beat can land on, in id order.
 *
 * [`feature_kind_ids`]'s counterpart for Phase 4's decision 1, and exported for
 * exactly the same reason: `web/src/world/types.ts` states `BEAT_ROAD`,
 * `BEAT_BOARD`, `BEAT_MOUTH` and `BEAT_GROUND` so a renderer can switch on a
 * number without an async boundary in front of a constant, and wave 1B wrote
 * them down as a **guess** at a projection `sim_core` had not grown yet.
 * `world/wasm.ts` compares its copy against this at startup rather than
 * trusting it.
 *
 * It is worth checking loudly for the reason the feature kinds are: nothing
 * would crash if a surface renumbered. The client would stage a body on a road
 * where a price moved on a board, and the only symptom would be a session that
 * read slightly wrong.
 *
 * A [`Surface`] is deliberately coarser than the [`Channel`](sim_core::history::spotlight::Channel)
 * a beat carries — seven channels land on four surfaces — and it is the surface
 * that crosses (`crate::flatten`'s `beats`).
 * @returns {Uint8Array}
 */
export function beat_surface_ids() {
    const ret = wasm.beat_surface_ids();
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * Returns the word for where a beat surfaces — road, board, mouth, ground — or
 * the empty string for an id this build does not ship.
 *
 * The half of the check that makes it a check of *meaning* rather than of
 * arithmetic: an id the client got right by accident while the ordering moved
 * under it would still name the wrong place.
 * @param {number} surface
 * @returns {string}
 */
export function beat_surface_name(surface) {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.beat_surface_name(surface);
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * Returns how close a hero has to be to take somebody along, in tiles.
 *
 * `sim_core::BECKON_REACH_TILES`, forwarded on [`strike_reach_tiles`]'s terms
 * and measured the same way — Chebyshev on the level with a tile of slack above
 * and below. It is **derived** sim-side rather than chosen: it is the tier's own
 * definition of *with somebody*, which is the same distance a follower calls
 * arrived at.
 * @returns {number}
 */
export function beckon_reach_tiles() {
    const ret = wasm.beckon_reach_tiles();
    return ret;
}

/**
 * Returns how tall a body at one life stage is drawn, in parts per thousand of
 * a grown one.
 *
 * **The export that deletes the client's stub scales** (Phase 7 plan, W2:
 * *"the client's I-6c stub constants are deleted by the exported curve"*). A
 * body's size belongs to the body plan and the body plan is data
 * (`sim_core::body`, on DF's `[BODY_SIZE:year:day:size]` growth curve); what
 * the client owned until wave 2A was one authored number for *child* and one
 * for its head, and the owner's ruling of 2026-08-29 called them a stub with
 * this as the named replacement.
 *
 * **Per mille rather than a fraction**, because the simulation's numbers are
 * integers (guide §9.2) and a scale is one of them: the cube root of the ratio
 * of body volumes, taken sim-side so no renderer anywhere takes a cube root of
 * a person.
 *
 * A thousand — grown — for an id this build does not ship.
 * @param {number} stage
 * @returns {number}
 */
export function body_scale_permille(stage) {
    const ret = wasm.body_scale_permille(stage);
    return ret >>> 0;
}

/**
 * Returns how far an arrow carries, in tiles.
 *
 * `sim_core::BOW_RANGE_TILES`, forwarded for [`strike_reach_tiles`]'s reason
 * and measured the same way — Chebyshev on the level with a tile of slack above
 * and below. **PROVISIONAL sim-side**: §8.1 gives a launcher a force, a cap and
 * four load timings and no range at all, so this is a number the host/sim split
 * forces rather than one DF answers, and forwarding it is what keeps the two
 * sides from inventing different ones.
 * @returns {number}
 */
export function bow_range_tiles() {
    const ret = wasm.bow_range_tiles();
    return ret;
}

/**
 * Returns how many tiles lie along one edge of a chunk.
 *
 * `sim_core::map::CHUNK_SIZE`. Sixteen, and a chunk is 16 × 16 × **1** — one
 * z-slice, not a cube (`docs/research/df-systems-map/systems/map-and-blocks.md`
 * §13.1: *"do not 'improve' it to 16×16×16"*), which is why a chunk coordinate's
 * third component is a tile z-level.
 *
 * Exported because this crate reports chunk *coordinates*
 * ([`TickSummary::dirty_chunks`]) and takes them
 * ([`WasmSim::mesh_chunk`]), and a client cannot turn the player's tile into one
 * without this number. A host that keeps its own copy — `web/src/world/types.ts`
 * states it as a TypeScript constant — should check it against this one at
 * startup rather than trusting that the two never drift.
 * @returns {number}
 */
export function chunk_size() {
    const ret = wasm.chunk_size();
    return ret;
}

/**
 * Returns the noun a commodity is called: food, or goods.
 * @param {number} commodity
 * @returns {string}
 */
export function commodity_name(commodity) {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.commodity_name(commodity);
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * Returns every feature kind id this build ships, in id order.
 *
 * So a client can check its own copy of the ids rather than trust it: a
 * renderer switches geometry on the kind, and a kind that quietly renumbered
 * would draw a grave where a farmstead burned. `web/src/world/types.ts` states
 * the numbers and `world/wasm.ts` compares them against this at startup,
 * exactly as it does for the chunk edge and the ramp shape.
 *
 * A `Uint8Array` on the JavaScript side. It is also how a client learns which
 * ids are *not* the simulation's: the market board it draws is its own prop
 * with an id of its own, and a build in which that number appeared here would
 * be a build drawing a grave where a board should stand.
 * @returns {Uint8Array}
 */
export function feature_kind_ids() {
    const ret = wasm.feature_kind_ids();
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * Returns the name of a mark on the ground — grave, burned-farmstead,
 * battlefield — or the empty string for an id this build does not ship.
 * @param {number} kind
 * @returns {string}
 */
export function feature_kind_name(kind) {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.feature_kind_name(kind);
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * Returns how many days past a blow a body's afflictions can still change.
 *
 * `sim_core::Afflictions::HEAL_DAYS_AT_SLOWEST` — the slowest tissue's healing
 * span, which is a season — forwarded on [`max_carried`]'s terms and for a
 * reason none of the others have: it is what a **guard** is sized against.
 *
 * A client that runs the world until the simulation says the player is up
 * (`web/src/host/rest.ts`, chunk Fx-5) has to know when waiting has stopped
 * being the answer. Pain is what is left of the wound and nothing else moves
 * it, so once this span has run every wound is at its floor and a body still
 * under is under for good — and a guard shorter than it would wake somebody a
 * morning before they were going to get up anyway. A number chosen on the far
 * side of this wall would be exactly the kind of second opinion the fortnight
 * this replaces turned out to be.
 *
 * **PROVISIONAL sim-side** in its magnitude (§14 Q19 leaves the curve
 * unrecovered), which is why it is forwarded rather than transcribed.
 * @returns {number}
 */
export function heal_days_at_slowest() {
    const ret = wasm.heal_days_at_slowest();
    return ret >>> 0;
}

/**
 * Installs the panic hook when the module is instantiated.
 *
 * Guide §14: panics abort on wasm and an abort poisons the instance, so the one
 * thing that has to survive is the *message*. The hook prints the panic and its
 * Rust stack to `console.error` before the trap, which is the difference
 * between a bug report and "the page froze".
 *
 * It runs from module init rather than from an exported `init()` the client has
 * to remember to call: a panic hook installed on the second line of the session
 * is a panic hook that missed the first line.
 */
export function install_panic_hook() {
    wasm.install_panic_hook();
}

/**
 * Returns every kind of item this build ships, in id order.
 *
 * DF's `item_type` numbering, gaps and all (`sim_core::ItemKind`) — which is
 * why a client that wants to switch a prop on one reads *this* rather than
 * counting: the ids run 0, 4, 5, 6, 20, 24, 54, 72, 89, 128 and nothing about
 * that list is derivable from its length.
 * @returns {Uint8Array}
 */
export function item_kind_ids() {
    const ret = wasm.item_kind_ids();
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * Returns the noun for a kind of item — boulder, log, blade — or the empty
 * string for an id this build does not ship.
 *
 * The word [`WasmSim::item_label`](crate::WasmSim::item_label) composes a whole
 * label out of, exported on its own for the one caller that has a *kind* and no
 * item: a client checking its ids at startup
 * (`web/src/world/wasm.ts`'s `checkConstants`). A panel row asks for the label.
 * @param {number} kind
 * @returns {string}
 */
export function item_kind_name(kind) {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.item_kind_name(kind);
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * Returns the noun a sentence in front of a thing of this kind and shape calls
 * it — *blade*, *bow*, *boulder* — or the empty string for a pair this build
 * does not ship.
 *
 * **[`item_kind_name`]'s sibling one axis over, and the difference matters**:
 * that one answers with the *class's* word, which a record has to use because a
 * record carries no shape, and this one answers with the word for the thing in
 * front of you (`sim_core::ItemKind::noun`, which
 * `sim_core::Snapshot::item_label` composes with a material). Asking the first
 * about a weapon gets *"weapon"*; asking this one gets *"blade"*.
 *
 * Per kind-and-shape rather than per item, for [`item_kind_name`]'s reason: it
 * is a fact about two rows of two tables, so a caller building a list of what
 * exists asks it once each and never again.
 * @param {number} kind
 * @param {number} shape
 * @returns {string}
 */
export function item_noun(kind, shape) {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.item_noun(kind, shape);
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * Returns `true` if a kind of item is one a pair of hands lifts.
 *
 * **The table the pickup verb takes from, published so that nobody keeps a
 * second copy of it** (`sim_core::ItemKind::is_portable`): a door, a table and
 * a library's shelf stay where they were put, and everything else this build
 * ships can be carried. `sim_core::hands` reads the same rows — a `take`
 * picks the first *portable* handle on the tile and refuses in silence when
 * there is none.
 *
 * It is exported because the **offer** is drawn on the other side of this wall
 * and the **refusal** is not drawn at all. Every furnished workshop has a table
 * standing on its work tile and every standing building has a door
 * (`sim_core::townplan`'s furnish pass), so a client that offered a pick-up for
 * whatever was lying on a tile put *"pick up brick table"* over a smithy's
 * bench and then watched the command come to nothing, which is the failure the
 * owner's playtest of 2026-08-30 reported. A client cannot be told to
 * transcribe the rows instead: they are one of the vocabularies devlog #2's
 * rule puts on this side, and a transcription is a second opinion the replay
 * format cannot afford.
 *
 * Asked per **kind** rather than per item, for [`item_kind_name`]'s reason: it
 * is a fact about a row of a table rather than about a thing in the world, so
 * there are ten possible answers and none of them moves while a session runs.
 *
 * `false` for an id this build does not ship, which is the safe direction as
 * well as the honest one ([`item_kind_name`]'s empty string): a kind nothing
 * here has heard of is a kind nothing here should promise to pick up.
 * @param {number} kind
 * @returns {boolean}
 */
export function item_portable(kind) {
    const ret = wasm.item_portable(kind);
    return ret !== 0;
}

/**
 * Returns every shape a kind of item comes in, in id order — or nothing at all
 * for an id this build does not ship.
 *
 * **DF's itemdef subtype, per kind** (`sim_core::ItemKind::shapes`). Twelve of
 * the fourteen answer with the one-element list holding
 * `sim_core::ItemShape::Plain` (id 0); the weapon answers with a blade and a
 * bow, which is the whole of why the axis exists.
 *
 * Exported for the developer's give door (chunk Fx-3, `web/src/dev/give.ts`),
 * which lists what it can mint by walking [`item_kind_ids`] and then this — so
 * the list a developer picks from is the simulation's own table twice over, and
 * **a shape the kind does not come in is a row that never appears** rather than
 * a command the world refuses in silence.
 * @param {number} kind
 * @returns {Uint8Array}
 */
export function item_shape_ids(kind) {
    const ret = wasm.item_shape_ids(kind);
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * Returns every kind of work the marketplace has, in id order.
 *
 * [`origin_kind_ids`]' counterpart for Phase 7's board. `web/src/world/types.ts`
 * states the ids so a scene can switch a working prop on a number without an
 * async boundary in front of a constant, and the hazard is the mild one rather
 * than the birth doors': a renumbered kind would put a woodcutter's swing at a
 * seam of rock, which is a screenshot that looks slightly wrong and nothing
 * worse. Checked at startup anyway, for exactly that reason — nothing would
 * crash.
 * @returns {Uint8Array}
 */
export function job_kind_ids() {
    const ret = wasm.job_kind_ids();
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * Returns the word for a kind of work — mine, fell, harvest, forge, carve,
 * cook, write, transcribe — or the empty string for an id this build does not
 * ship.
 *
 * The meaning half of [`job_kind_ids`]' check ([`beat_surface_name`]'s
 * argument). A debug label rather than anything a player reads: what a scene
 * says about somebody working is a body with a tool in its hands.
 * @param {number} kind
 * @returns {string}
 */
export function job_kind_name(kind) {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.job_kind_name(kind);
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * Returns `true` if a body at one life stage swings at anybody.
 *
 * `sim_core::body::LifeStage::fights`, forwarded beside
 * [`life_stage_portable`] and for its reason one verb over: the offer is drawn
 * on the other side of this wall and the refusal is not drawn at all, so a
 * client that put a strike prompt over everybody would offer a nine-year-old a
 * swing the simulation then refuses in silence.
 *
 * **It is the same row an NPC child is refused by**, which is rider 2's ruling
 * 1 — *stage capabilities apply to the player exactly as to NPCs* — kept as one
 * table asked from both sides rather than as a rule stated twice.
 *
 * `false` for an id this build does not ship, which is the safe direction and
 * the honest one: a stage nothing here has heard of is a stage nothing here
 * should promise a fight to.
 * @param {number} stage
 * @returns {boolean}
 */
export function life_stage_fights(stage) {
    const ret = wasm.life_stage_fights(stage);
    return ret !== 0;
}

/**
 * Returns `true` if a body at one life stage is small enough to be carried.
 *
 * `sim_core::body::LifeStage::is_portable` over the size curve, forwarded
 * beside [`item_portable`] and for its reason: the **offer** is drawn on the
 * other side of this wall and the **refusal** is not drawn at all, so a client
 * that offered *pick up* over everybody would put the verb over an adult and
 * then watch the command come to nothing.
 *
 * **Not a baby rule** (Phase 7 plan, rider 2): the answer comes off the size
 * curve, so a corpse, a wounded body and a smaller race's adult all arrive at
 * the same door without a second clause.
 *
 * `false` for an id this build does not ship, which is the safe direction as
 * well as the honest one: a stage nothing here has heard of is a stage nothing
 * here should promise to pick up.
 * @param {number} stage
 * @returns {boolean}
 */
export function life_stage_portable(stage) {
    const ret = wasm.life_stage_portable(stage);
    return ret !== 0;
}

/**
 * Returns the word a plate calls a life stage, or the empty string for a grown
 * one.
 *
 * **The simulation's own vocabulary, not the client's** — the same rule
 * [`item_kind_name`](crate::item_kind_name) follows, and the reason the client
 * stopped spelling *"(child)"* itself: a plate that says *toddler* is saying
 * something the stage table decided, and a second copy of the four words would
 * go stale the day a race has five.
 *
 * **The empty string is an answer**: an adult is unmarked, a name on its own,
 * which is DF's `general_baby_name` / `general_child_name` split with the third
 * row rider 2 added. The empty string is also what an id this build does not
 * ship gets, which is the same shape [`item_kind_name`](crate::item_kind_name)
 * uses for the same reason.
 * @param {number} stage
 * @returns {string}
 */
export function life_stage_word(stage) {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.life_stage_word(stage);
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * Returns how many ticks a hand waits between shots.
 *
 * `sim_core::LOOSE_COOLDOWN_TICKS`, which is `strike_cooldown_ticks` exactly
 * and is forwarded separately anyway: it is **one pair of hands and one clock**
 * sim-side, and a client that assumed the two were the same number would be
 * assuming rather than reading. The day a bow's own draw time exists (§8.1's
 * `beginner_load_time`/`advanced_load_time`, the documented trim) this moves and
 * the other does not.
 * @returns {number}
 */
export function loose_cooldown_ticks() {
    const ret = wasm.loose_cooldown_ticks();
    return ret >>> 0;
}

/**
 * Returns every kind of building a town plan has, in id order.
 *
 * [`job_kind_ids`]' sibling for the other vocabulary a town's buildings are
 * read by ([`WasmSim::lots`](crate::WasmSim::lots)).
 *
 * **The browser client switches on none of them**, which is the arrangement
 * worth aiming at: it offers the workshop verb where a lot's own `trade`
 * column says somebody works there and labels it with
 * [`lot_kind_name`]'s word, so it keeps no copy of this table and there is
 * nothing on that side to drift. The ids are exported for the client that does
 * want one — a scene that drew a forge differently from a tavern would — and
 * they are append-only, so one a build does not know is a building a later one
 * added.
 * @returns {Uint8Array}
 */
export function lot_kind_ids() {
    const ret = wasm.lot_kind_ids();
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * Returns the word for a kind of building, or the empty string for an id this
 * build does not ship.
 * @param {number} kind
 * @returns {string}
 */
export function lot_kind_name(kind) {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.lot_kind_name(kind);
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * Returns how many things one pair of hands carries at once.
 *
 * `sim_core::MAX_CARRIED`, forwarded on [`max_chronicle_entries`]' terms and
 * for its reason: the simulation enforces the bound whatever the client
 * believes, and knowing it is the difference between a panel that says the
 * hands are full and a verb that quietly stops working.
 *
 * **PROVISIONAL sim-side** — the core owns the number and its own note says
 * why it is eight and what will move it (weight, when bodies arrive) — so this
 * is exactly the shape a cap should have on this boundary: one home, one
 * forwarding line, and a startup check on the client's copy.
 * @returns {number}
 */
export function max_carried() {
    const ret = wasm.max_carried();
    return ret >>> 0;
}

/**
 * Returns how many records one chronicle can hold.
 *
 * `sim_core::history::MAX_CHRONICLE_ENTRIES`. Exported for
 * [`chunk_size`]'s reason and one more: a client compiling a book out of a
 * player's journal has to know when to stop offering pages, and the only
 * alternative to asking is writing the number down a second time. The
 * simulation enforces it either way — a seventeenth citation is dropped by the
 * type that carries them ([`WasmSim::queue_place_chronicle`]) — so this is not
 * a guard, it is what lets a client say so *before* a page goes missing.
 *
 * `web/src/world/types.ts` states the number and checks it against this at
 * startup, exactly as it does for the chunk edge and the ramp shape.
 * @returns {number}
 */
export function max_chronicle_entries() {
    const ret = wasm.max_chronicle_entries();
    return ret >>> 0;
}

/**
 * Returns how many seats one origin card carries.
 *
 * `sim_core::history::origins::MAX_CLAIMS`, forwarded beside [`max_origins`].
 * The bound on a card's claim count and on [`WasmSim::claims`]' answer alike —
 * a parent holds at most the two seats a settlement has, and a pair of them
 * reaches four.
 * @returns {number}
 */
export function max_claims() {
    const ret = wasm.max_claims();
    return ret >>> 0;
}

/**
 * Returns how many people one hero may have at their back.
 *
 * `sim_core::MAX_COMPANIONS`, forwarded on [`max_carried`]' terms and for its
 * exact reason: the simulation refuses the fifth beckon in silence, so a client
 * that did not know the number would draw a prompt over somebody it cannot
 * take along. **PROVISIONAL sim-side.**
 * @returns {number}
 */
export function max_companions() {
    const ret = wasm.max_companions();
    return ret >>> 0;
}

/**
 * Returns how many lives the birth screen may offer at once.
 *
 * `sim_core::history::origins::MAX_ORIGINS`, forwarded — public in the core
 * from the day the projection shipped, *because* of what the saga's two caps
 * cost a wave earlier (that constant's own note). A client sizes the screen
 * before it has a list, and [`WasmSim::origins`] never hands over more rows than
 * this.
 * @returns {number}
 */
export function max_origins() {
    const ret = wasm.max_origins();
    return ret >>> 0;
}

/**
 * Returns how many bytes of typed name the simulation's wall keeps.
 *
 * `sim_core::history::PlayerName::MAX_BYTES`, and **bytes rather than
 * characters** because that is what the wall promises: a longer name is cut at
 * the last character boundary that fits, never mid-character
 * ([`WasmSim::queue_claim`]).
 *
 * A client that shows a name field wants it for the same reason
 * [`max_chronicle_entries`] exists — the cap is enforced sim-side whatever the
 * client does, and knowing it is the difference between a player being told and
 * a player watching their name get shorter.
 * @returns {number}
 */
export function max_player_name_bytes() {
    const ret = wasm.max_player_name_bytes();
    return ret >>> 0;
}

/**
 * Returns how many ages one Book of Eras carries.
 *
 * `sim_core::history::saga::MAX_SAGA_ERAS`, forwarded. Wave 2B shipped this as
 * a **transcription** — the cap was private to that module, so `crate::flatten`
 * wrote the number down and a `debug_assert` held the two sides together — and
 * wave 3A made it public at this crate's ask, which is what turned a number with
 * two homes into one forwarding line (`max_chronicle_entries`' arrangement, a
 * wave later).
 *
 * Exported for [`chunk_size`]'s reason: a client lays the book out before it has
 * a book, and `web/src/world/types.ts` states the number and checks it here at
 * startup rather than trusting it. Worth checking loudly for
 * [`ramp_shape_id`]'s reason — nothing would crash if it drifted, and a client
 * laying the book out against a stale bound would silently show a shorter
 * history than the world has, which reads as a quiet world rather than as a bug.
 * @returns {number}
 */
export function max_saga_eras() {
    const ret = wasm.max_saga_eras();
    return ret >>> 0;
}

/**
 * Returns how many records one line of the Book of Eras narrates.
 *
 * `sim_core::history::saga::MAX_LINE_CITES`, forwarded with
 * [`max_saga_eras`] and for the same reason — with one difference worth
 * knowing: **the layout does not depend on it**. A line's citations cross behind
 * a count ([`WasmSim::era_citations`]), so a line that grew a third would arrive
 * whole and only this number would be stale. What it is for is a client sizing a
 * trace row before it has one.
 *
 * The export keeps its wave 2B name, which is longer than the core's: what it
 * bounds on this side is a *saga line*, and a client that already imports
 * `max_chronicle_entries` should not have to remember which module a bare
 * `max_line_cites` belonged to.
 * @returns {number}
 */
export function max_saga_line_cites() {
    const ret = wasm.max_saga_line_cites();
    return ret >>> 0;
}

/**
 * Returns every door a player can be born through, in id order.
 *
 * [`beat_surface_ids`]' counterpart for Phase 6's decision 3, exported for the
 * same reason and with the same hazard. `web/src/world/types.ts` states
 * `ORIGIN_ORPHAN`, `ORIGIN_UNRECORDED` and `ORIGIN_BOUND` so the start flow can
 * switch on a number without an async boundary in front of a constant — and the
 * switch decides *which command the card is handed back to*
 * ([`WasmSim::queue_born_orphan`](crate::WasmSim::queue_born_orphan) and its
 * two siblings), so a renumbered door would send a bound child's figure number
 * through the orphan's settlement argument. That is a birth the simulation
 * refuses in silence, which is the worst shape a drift can take here.
 *
 * A `Uint8Array` on the JavaScript side, checked at startup rather than
 * trusted.
 * @returns {Uint8Array}
 */
export function origin_kind_ids() {
    const ret = wasm.origin_kind_ids();
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * Returns the word for a door into the world — orphan, unrecorded, bound — or
 * the empty string for an id this build does not ship.
 *
 * The half of the check that makes it a check of *meaning*
 * ([`beat_surface_name`]): an id the client got right by accident while the
 * ordering moved under it would still name the wrong life. It is a debug label
 * rather than a card's heading — what a card *says* is composed from the record
 * it cites, in the simulation's own register.
 * @param {number} kind
 * @returns {string}
 */
export function origin_kind_name(kind) {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.origin_kind_name(kind);
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * Returns the verb for which way a price moved: rose, or fell.
 * @param {number} direction
 * @returns {string}
 */
export function price_move_name(direction) {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.price_move_name(direction);
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * Returns every rung of the quality ladder, in id order.
 *
 * DF's `item_quality`, all seven ([`item_kind_name`]'s sibling). Exported with
 * the other two Phase 7 vocabularies so a client that groups an inventory by
 * how well a thing was made checks its own copy of the ladder rather than
 * trusting it — the rungs are hashed into every item, so a renumbered one is a
 * replay-format break and exactly the kind of number worth checking loudly.
 * @returns {Uint8Array}
 */
export function quality_ids() {
    const ret = wasm.quality_ids();
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * Returns the shape id a ramp reports through
 * [`WasmSim::tile_shape`](WasmSim::tile_shape).
 *
 * `sim_core::map::TileShape::Ramp`, which is DF's `RAMP` and therefore 10
 * (`docs/research/df-systems-map/systems/map-and-blocks.md` §5.3). One id
 * rather than the whole table, because one is what a client has to *act* on: a
 * ramp was the only shipped shape that was not a box, so it was the only one
 * whose collision differed from "does this cube block me"
 * ([`WasmSim::ramp_rise_direction`]). Wave 1.5 shipped a second
 * ([`stair_shape_id`]).
 *
 * Exported for the reason [`chunk_size`] is: `web/src/world/types.ts` states the
 * number itself so that shape arithmetic does not sit behind an async boundary,
 * and checks its copy against this one at startup rather than trusting that the
 * two never drift. A shape id reaches the world hash, so drifting is a
 * replay-format break — which makes it exactly the kind of number worth
 * checking loudly.
 * @returns {number}
 */
export function ramp_shape_id() {
    const ret = wasm.ramp_shape_id();
    return ret;
}

/**
 * Returns the shape id a **climbing stair** reports through
 * [`WasmSim::tile_shape`](WasmSim::tile_shape).
 *
 * `sim_core::map::TileShape::StairUp`, which is DF's `STAIR_UP` and therefore 7
 * (map-and-blocks.md §5.3). [`ramp_shape_id`]'s twin, exported for its reason
 * and shipped in the same wave the shape was first put in the ground: wave
 * 1.5's grading pass replaces a ramp with a flight on every one-level step
 * **between built surfaces** (`sim_core::worldgen`'s `built_surface`, read from
 * both ends of the step since wave 1.6), so a client that reads a footing off
 * the ramp id alone stands a walker at the bottom of every step in every town.
 *
 * One id and not three: the two stairs that go *down* are the head of a flight
 * and are never the tile a body stands on to climb, so
 * [`WasmSim::ramp_rise_direction`] answers about this one and a client needs no
 * word for the others.
 * @returns {number}
 */
export function stair_shape_id() {
    const ret = wasm.stair_shape_id();
    return ret;
}

/**
 * Returns the word for how deep a settlement's store of something is: famine,
 * dear, steady, plenty.
 * @param {number} band
 * @returns {string}
 */
export function stock_band_name(band) {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.stock_band_name(band);
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * Returns how many ticks a hand waits between swings.
 *
 * `sim_core::STRIKE_COOLDOWN_TICKS`, forwarded for
 * [`strike_reach_tiles`]'s reason and used for a different one: a client shows
 * **readiness** rather than refusing a press. A crosshair that says *ready* and
 * then *a moment* is a player who understands the cadence they are fighting at;
 * a key that silently does nothing three times out of four is the graybox
 * ruling's own example of a verb that teaches a player to stop pressing it.
 *
 * It is `combat::ANSWER_TICKS` sim-side — the rate the world answers a blow at
 * — so a player and a townsman trade at the same speed.
 * @returns {number}
 */
export function strike_cooldown_ticks() {
    const ret = wasm.strike_cooldown_ticks();
    return ret >>> 0;
}

/**
 * Returns how far a blow reaches, in tiles.
 *
 * `sim_core::STRIKE_REACH_TILES`, forwarded on [`max_carried`]'s terms and for
 * a sharper version of its reason. The simulation refuses a swing across a
 * distance it does not like and **a tick has no error channel**, so a client
 * that did not know the number would have exactly two options: offer a strike
 * prompt over anybody in sight and let a third of them do nothing, or invent a
 * range of its own and disagree with the simulation about which.
 *
 * Chebyshev on the level, plus one tile of slack above or below it — the sim's
 * own predicate, and a client applying this number applies that rule. It is
 * **PROVISIONAL sim-side**, which is exactly why it is forwarded rather than
 * transcribed.
 * @returns {number}
 */
export function strike_reach_tiles() {
    const ret = wasm.strike_reach_tiles();
    return ret;
}

/**
 * Returns how sure a speaker at that many tellings' distance can be:
 * first-hand, reported, distant, hearsay.
 *
 * For a debug view rather than for the dialogue: the *line* already carries its
 * telling in the words it uses ([`WasmSim::speak`]), and this is what a
 * traceability panel puts in the column beside the hop count — the same word
 * `sim-cli legends --rumors` prints.
 * @param {number} hops
 * @returns {string}
 */
export function telling_name(hops) {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.telling_name(hops);
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * Returns how many ticks a walker spends crossing one tile.
 *
 * `sim_core::TICKS_PER_STEP`, forwarded on [`max_carried`]' terms and for a
 * reason that is presentation rather than legality: the local tier moves a body
 * a whole tile at a time, on this cadence, and a client that drew each of those
 * moves the moment it happened would draw a crowd teleporting — which is what
 * the wave-1 playtest saw. Knowing the cadence is what lets the renderer glide
 * a body across the tile the simulation already moved them over.
 *
 * **It is not a licence to predict.** The client interpolates *between two
 * positions the simulation has already stated* and never past the second one;
 * where the body goes next is the sim's business and arrives when it arrives
 * (`witness/scenes.ts`).
 *
 * Derived sim-side from the calendar and the body plan's gait rows
 * (`sim_core::body`), so a retune of the day moves the pace and this line with
 * it.
 *
 * **It is the grown human's pace, and since wave 2A it is not the only one.**
 * A child walks at fifteen ticks a tile and a toddler at twenty-four; the
 * per-stage export beside it ([`walk_ticks_per_step_for_stage`]) is what a
 * client smoothing a crowd of mixed ages reads. This one is kept — unchanged
 * in value and in meaning — because a session has to size its smoothing before
 * it has any bodies to size it against, and because a client that only ever
 * draws adults should not have to ask a stage-shaped question.
 * @returns {number}
 */
export function walk_ticks_per_step() {
    const ret = wasm.walk_ticks_per_step();
    return ret >>> 0;
}

/**
 * Returns how many ticks a body at one life stage spends crossing a tile.
 *
 * `sim_core::body::LifeStage::ticks_per_step`, forwarded on
 * [`walk_ticks_per_step`]'s terms: the pace is the simulation's, the smoothing
 * is the client's, and a client that wrote four numbers down would be keeping a
 * copy of a gait table that moves when the day does.
 *
 * Asked per **stage** rather than per figure, for [`item_portable`]'s reason: it
 * is a fact about a row of a table rather than about a body in the world, so
 * there are four possible answers and none of them moves while a session runs.
 * Which stage a given body is in rides its presence row
 * ([`WasmSim::present_figures`]).
 *
 * The adult's pace for an id this build does not ship, which is the safe
 * direction: a body drawn at the wrong pace is a body that rubber-bands, and a
 * pace of zero would be a division by zero in the smoothing.
 * @param {number} stage
 * @returns {number}
 */
export function walk_ticks_per_step_for_stage(stage) {
    const ret = wasm.walk_ticks_per_step_for_stage(stage);
    return ret >>> 0;
}

/**
 * Returns the highest z-level any generated column reaches.
 *
 * `sim_core::WORLD_Z_MAX`; the other end of the band [`world_z_min`] starts.
 * Both ends are inclusive, so the world is
 * `world_z_max() - world_z_min() + 1` levels tall.
 * @returns {number}
 */
export function world_z_max() {
    const ret = wasm.world_z_max();
    return ret;
}

/**
 * Returns the lowest z-level any generated column reaches.
 *
 * `sim_core::WORLD_Z_MIN`. A world constant rather than a per-column one: every
 * column spans the same band, which is what stops a renderer meeting a hole at
 * a level where the neighbouring column has terrain (`sim_core`'s worldgen
 * fields).
 *
 * # Why a function and not a method
 *
 * It is the same number for every seed and every session, so hanging it off
 * [`WasmSim`] would suggest it depends on a world — and a client wants it
 * *before* it has one, to size a camera clip range or a z-slider. It is a
 * function rather than an exported constant because wasm-bindgen's exports are
 * functions; read it once at startup and keep the number.
 * @returns {number}
 */
export function world_z_min() {
    const ret = wasm.world_z_min();
    return ret;
}
function __wbg_get_imports() {
    const import0 = {
        __proto__: null,
        __wbg_Error_408e67f47ca7b58b: function(arg0, arg1) {
            const ret = Error(getStringFromWasm0(arg0, arg1));
            return ret;
        },
        __wbg___wbindgen_throw_bb96b2010945f0bc: function(arg0, arg1) {
            throw new Error(getStringFromWasm0(arg0, arg1));
        },
        __wbg_error_757e9472f8410341: function(arg0, arg1) {
            let deferred0_0;
            let deferred0_1;
            try {
                deferred0_0 = arg0;
                deferred0_1 = arg1;
                console.error(getStringFromWasm0(arg0, arg1));
            } finally {
                wasm.__wbindgen_free(deferred0_0, deferred0_1, 1);
            }
        },
        __wbg_new_227d7c05414eb861: function() {
            const ret = new Error();
            return ret;
        },
        __wbg_stack_3b0d974bbf31e44f: function(arg0, arg1) {
            const ret = arg1.stack;
            const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len1 = WASM_VECTOR_LEN;
            getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
        },
        __wbindgen_cast_0000000000000001: function(arg0, arg1) {
            // Cast intrinsic for `Ref(String) -> Externref`.
            const ret = getStringFromWasm0(arg0, arg1);
            return ret;
        },
        __wbindgen_init_externref_table: function() {
            const table = wasm.__wbindgen_externrefs;
            const offset = table.grow(4);
            table.set(0, undefined);
            table.set(offset + 0, undefined);
            table.set(offset + 1, null);
            table.set(offset + 2, true);
            table.set(offset + 3, false);
        },
    };
    return {
        __proto__: null,
        "./sim_wasm_bg.js": import0,
    };
}

const ChunkMeshFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_chunkmesh_free(ptr, 1));
const SpokenFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_spoken_free(ptr, 1));
const TickSummaryFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_ticksummary_free(ptr, 1));
const WasmSimFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_wasmsim_free(ptr, 1));

function getArrayF32FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getFloat32ArrayMemory0().subarray(ptr / 4, ptr / 4 + len);
}

function getArrayF64FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getFloat64ArrayMemory0().subarray(ptr / 8, ptr / 8 + len);
}

function getArrayI32FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getInt32ArrayMemory0().subarray(ptr / 4, ptr / 4 + len);
}

function getArrayJsValueFromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    const mem = getDataViewMemory0();
    const result = [];
    for (let i = ptr; i < ptr + 4 * len; i += 4) {
        result.push(wasm.__wbindgen_externrefs.get(mem.getUint32(i, true)));
    }
    wasm.__externref_drop_slice(ptr, len);
    return result;
}

function getArrayU16FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getUint16ArrayMemory0().subarray(ptr / 2, ptr / 2 + len);
}

function getArrayU32FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getUint32ArrayMemory0().subarray(ptr / 4, ptr / 4 + len);
}

function getArrayU8FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getUint8ArrayMemory0().subarray(ptr / 1, ptr / 1 + len);
}

let cachedDataViewMemory0 = null;
function getDataViewMemory0() {
    if (cachedDataViewMemory0 === null || cachedDataViewMemory0.buffer.detached === true || (cachedDataViewMemory0.buffer.detached === undefined && cachedDataViewMemory0.buffer !== wasm.memory.buffer)) {
        cachedDataViewMemory0 = new DataView(wasm.memory.buffer);
    }
    return cachedDataViewMemory0;
}

let cachedFloat32ArrayMemory0 = null;
function getFloat32ArrayMemory0() {
    if (cachedFloat32ArrayMemory0 === null || cachedFloat32ArrayMemory0.byteLength === 0) {
        cachedFloat32ArrayMemory0 = new Float32Array(wasm.memory.buffer);
    }
    return cachedFloat32ArrayMemory0;
}

let cachedFloat64ArrayMemory0 = null;
function getFloat64ArrayMemory0() {
    if (cachedFloat64ArrayMemory0 === null || cachedFloat64ArrayMemory0.byteLength === 0) {
        cachedFloat64ArrayMemory0 = new Float64Array(wasm.memory.buffer);
    }
    return cachedFloat64ArrayMemory0;
}

let cachedInt32ArrayMemory0 = null;
function getInt32ArrayMemory0() {
    if (cachedInt32ArrayMemory0 === null || cachedInt32ArrayMemory0.byteLength === 0) {
        cachedInt32ArrayMemory0 = new Int32Array(wasm.memory.buffer);
    }
    return cachedInt32ArrayMemory0;
}

function getStringFromWasm0(ptr, len) {
    return decodeText(ptr >>> 0, len);
}

let cachedUint16ArrayMemory0 = null;
function getUint16ArrayMemory0() {
    if (cachedUint16ArrayMemory0 === null || cachedUint16ArrayMemory0.byteLength === 0) {
        cachedUint16ArrayMemory0 = new Uint16Array(wasm.memory.buffer);
    }
    return cachedUint16ArrayMemory0;
}

let cachedUint32ArrayMemory0 = null;
function getUint32ArrayMemory0() {
    if (cachedUint32ArrayMemory0 === null || cachedUint32ArrayMemory0.byteLength === 0) {
        cachedUint32ArrayMemory0 = new Uint32Array(wasm.memory.buffer);
    }
    return cachedUint32ArrayMemory0;
}

let cachedUint8ArrayMemory0 = null;
function getUint8ArrayMemory0() {
    if (cachedUint8ArrayMemory0 === null || cachedUint8ArrayMemory0.byteLength === 0) {
        cachedUint8ArrayMemory0 = new Uint8Array(wasm.memory.buffer);
    }
    return cachedUint8ArrayMemory0;
}

function isLikeNone(x) {
    return x === undefined || x === null;
}

function passArray32ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 4, 4) >>> 0;
    getUint32ArrayMemory0().set(arg, ptr / 4);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

function passArrayF64ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 8, 8) >>> 0;
    getFloat64ArrayMemory0().set(arg, ptr / 8);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

function passStringToWasm0(arg, malloc, realloc) {
    if (realloc === undefined) {
        const buf = cachedTextEncoder.encode(arg);
        const ptr = malloc(buf.length, 1) >>> 0;
        getUint8ArrayMemory0().subarray(ptr, ptr + buf.length).set(buf);
        WASM_VECTOR_LEN = buf.length;
        return ptr;
    }

    let len = arg.length;
    let ptr = malloc(len, 1) >>> 0;

    const mem = getUint8ArrayMemory0();

    let offset = 0;

    for (; offset < len; offset++) {
        const code = arg.charCodeAt(offset);
        if (code > 0x7F) break;
        mem[ptr + offset] = code;
    }
    if (offset !== len) {
        if (offset !== 0) {
            arg = arg.slice(offset);
        }
        ptr = realloc(ptr, len, len = offset + arg.length * 3, 1) >>> 0;
        const view = getUint8ArrayMemory0().subarray(ptr + offset, ptr + len);
        const ret = cachedTextEncoder.encodeInto(arg, view);

        offset += ret.written;
        ptr = realloc(ptr, len, offset, 1) >>> 0;
    }

    WASM_VECTOR_LEN = offset;
    return ptr;
}

function takeFromExternrefTable0(idx) {
    const value = wasm.__wbindgen_externrefs.get(idx);
    wasm.__externref_table_dealloc(idx);
    return value;
}

let cachedTextDecoder = new TextDecoder('utf-8', { ignoreBOM: true, fatal: true });
cachedTextDecoder.decode();
const MAX_SAFARI_DECODE_BYTES = 2146435072;
let numBytesDecoded = 0;
function decodeText(ptr, len) {
    numBytesDecoded += len;
    if (numBytesDecoded >= MAX_SAFARI_DECODE_BYTES) {
        cachedTextDecoder = new TextDecoder('utf-8', { ignoreBOM: true, fatal: true });
        cachedTextDecoder.decode();
        numBytesDecoded = len;
    }
    return cachedTextDecoder.decode(getUint8ArrayMemory0().subarray(ptr, ptr + len));
}

const cachedTextEncoder = new TextEncoder();

if (!('encodeInto' in cachedTextEncoder)) {
    cachedTextEncoder.encodeInto = function (arg, view) {
        const buf = cachedTextEncoder.encode(arg);
        view.set(buf);
        return {
            read: arg.length,
            written: buf.length
        };
    };
}

let WASM_VECTOR_LEN = 0;

let wasmModule, wasmInstance, wasm;
function __wbg_finalize_init(instance, module) {
    wasmInstance = instance;
    wasm = instance.exports;
    wasmModule = module;
    cachedDataViewMemory0 = null;
    cachedFloat32ArrayMemory0 = null;
    cachedFloat64ArrayMemory0 = null;
    cachedInt32ArrayMemory0 = null;
    cachedUint16ArrayMemory0 = null;
    cachedUint32ArrayMemory0 = null;
    cachedUint8ArrayMemory0 = null;
    wasm.__wbindgen_start();
    return wasm;
}

async function __wbg_load(module, imports) {
    if (typeof Response === 'function' && module instanceof Response) {
        if (!module.ok) {
            throw new Error(`failed to fetch Wasm: ${module.status} ${module.statusText} fetching '${module.url}'`);
        }

        if (typeof WebAssembly.instantiateStreaming === 'function') {
            try {
                return await WebAssembly.instantiateStreaming(module, imports);
            } catch (e) {
                const validResponse = expectedResponseType(module.type);

                if (validResponse && module.headers.get('Content-Type') !== 'application/wasm') {
                    console.warn("`WebAssembly.instantiateStreaming` failed because your server does not serve Wasm with `application/wasm` MIME type. Falling back to `WebAssembly.instantiate` which is slower. Original error:\n", e);

                } else { throw e; }
            }
        }

        const bytes = await module.arrayBuffer();
        return await WebAssembly.instantiate(bytes, imports);
    } else {
        const instance = await WebAssembly.instantiate(module, imports);

        if (instance instanceof WebAssembly.Instance) {
            return { instance, module };
        } else {
            return instance;
        }
    }

    function expectedResponseType(type) {
        switch (type) {
            case 'basic': case 'cors': case 'default': return true;
        }
        return false;
    }
}

function initSync(module) {
    if (wasm !== undefined) return wasm;


    if (module !== undefined) {
        if (Object.getPrototypeOf(module) === Object.prototype) {
            ({module} = module)
        } else {
            console.warn('using deprecated parameters for `initSync()`; pass a single object instead')
        }
    }

    const imports = __wbg_get_imports();
    if (!(module instanceof WebAssembly.Module)) {
        module = new WebAssembly.Module(module);
    }
    const instance = new WebAssembly.Instance(module, imports);
    return __wbg_finalize_init(instance, module);
}

async function __wbg_init(module_or_path) {
    if (wasm !== undefined) return wasm;


    if (module_or_path !== undefined) {
        if (Object.getPrototypeOf(module_or_path) === Object.prototype) {
            ({module_or_path} = module_or_path)
        } else {
            console.warn('using deprecated parameters for the initialization function; pass a single object instead')
        }
    }

    if (module_or_path === undefined) {
        module_or_path = new URL('sim_wasm_bg.wasm', import.meta.url);
    }
    const imports = __wbg_get_imports();

    if (typeof module_or_path === 'string' || (typeof Request === 'function' && module_or_path instanceof Request) || (typeof URL === 'function' && module_or_path instanceof URL)) {
        module_or_path = fetch(module_or_path);
    }

    const { instance, module } = await __wbg_load(await module_or_path, imports);

    return __wbg_finalize_init(instance, module);
}

export { initSync, __wbg_init as default };
